/**
 * Product Detail Page Script
 * Handles fetching, rendering, and interactions for individual product pages
 */

// Console components. Under `body.console` the related listings are the same
// tile the feed draws, and the price visualisation is the market ladder --
// see console-product.js. The legacy renderers below are untouched and still
// run with the flag off.
import { grid as consoleGrid, toCard } from './console-cards.js';
import { ladderHtml } from './console-product.js';
import { trust as sellerTrust } from './trust-score.js';

/** Is the Console shell on? */
function consoleOn() {
  return !!(document.body && document.body.classList.contains('console'));
}

// Initialize logger for this module
const logger = window.Logger ? window.Logger.create('product-detail') : {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: console.error.bind(console)
};

/**
 * Shared freshness policy (script.js defines ufGetFreshness and loads first on
 * product pages). Falls back to 'live' so a missing script.js degrades to the
 * old "show the listing" behaviour rather than marking everything sold.
 */
function listingFreshness(item) {
  return window.ufGetFreshness
    ? window.ufGetFreshness(item)
    : { state: 'live', verifiedAt: null, hoursSinceVerified: null };
}

class ProductDetailManager {
  constructor() {
    this.productId = null;
    this.normalizedId = null;  // New
    this.productData = null;
    this.relatedListings = [];
    this.currentUser = null;
    this.currentUserVote = undefined; // Track the user's current vote state (undefined = not yet loaded)
    this.hasContributed = false;  // New
    this.activeConditionFilter = 'all'; // Condition filter state
    this.activeSortMode = 'best-price'; // Sort mode: 'best-price' or 'most-recent'
    
    // DOM elements
    this.loadingState = document.getElementById('loadingState');
    this.errorState = document.getElementById('errorState');
    this.productContent = document.getElementById('productDetailContent');
    
    this.init();
  }
  
  async init() {
    logger.info('[ProductDetail] init() called');
    
    // Wait for API service to be available (with retry)
    let retries = 0;
    const maxRetries = 10;
    while (!window.apiService && retries < maxRetries) {
      logger.info(`[ProductDetail] Waiting for API service... (attempt ${retries + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
    }
    
    if (!window.apiService) {
      logger.error('[ProductDetail] API service not available after waiting');
      this.showError('Service initialization error. Please refresh the page.');
      return;
    }
    logger.info('[ProductDetail] API service available');
    
    // Get product ID from URL
    this.productId = this.getProductIdFromURL();
    logger.info('[ProductDetail] Extracted productId from URL:', this.productId);
    logger.info('[ProductDetail] isMasterParent:', this.isMasterParent, 'masterParentId:', this.masterParentId);
    
    if (!this.productId) {
      logger.error('[ProductDetail] No product ID found in URL');
      this.showError('No product ID provided in URL.');
      return;
    }
    
    // Check if user is logged in (try multiple times in case accountManager isn't ready)
    if (window.accountManager && window.accountManager.currentUser) {
      this.currentUser = window.accountManager.currentUser;
    } else {
      // Wait a bit for accountManager to initialize
      await new Promise(resolve => setTimeout(resolve, 100));
      if (window.accountManager && window.accountManager.currentUser) {
        this.currentUser = window.accountManager.currentUser;
      }
    }
    
    // Load product data
    await this.loadProduct();
    
    // Initialize event listeners (after product is rendered)
    this.initEventListeners();
    
    // On full page load, token may be empty until refresh; establish session so vote UI shows logged-in
    if (!this.currentUser && window.apiService && typeof window.apiService.checkAuth === 'function') {
      try {
        const ok = await window.apiService.checkAuth();
        if (ok) {
          this.currentUser = (window.accountManager && window.accountManager.currentUser) || null;
          if (!this.currentUser && typeof window.apiService.getCurrentUser === 'function') {
            const userData = await window.apiService.getCurrentUser();
            this.currentUser = (userData && userData.id) ? userData : null;
            if (window.accountManager && this.currentUser) {
              window.accountManager.currentUser = this.currentUser;
            }
          }
          if (this.currentUser && this.productData) {
            this.renderVoting(this.productData);
          }
        }
      } catch (e) {
        // ignore
      }
    }
  }
  
  /**
   * Extract product ID from URL parameters
   */
  getProductIdFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    this.normalizedId = urlParams.get('normalized_id');  // New
    let idParam = urlParams.get('id') || urlParams.get('product_id');

    // Canonical path shape /p/<slug>-<id>/ — the trailing id is the key.
    if (!idParam) {
      const pathMatch = window.location.pathname.match(/^\/p\/.*-(\d+)\/?$/);
      if (pathMatch) {
        idParam = `master_parent_${pathMatch[1]}`;
      }
    }

    if (!idParam) {
      this.isMasterParent = false;
      return null;
    }
    
    // Check if this is a MasterParent ID (must start with "master_parent_")
    if (idParam.startsWith('master_parent_')) {
      this.isMasterParent = true;
      // Extract numeric ID
      this.masterParentId = idParam.replace('master_parent_', '');
      logger.info('[ProductDetail] Detected Master Parent ID:', this.masterParentId);
      return this.masterParentId; // Return the extracted ID so init() doesn't fail
    }
    
    // Regular product ID
    this.isMasterParent = false;
    this.masterParentId = null;
    return idParam;
  }
  
  /**
   * Get cache key for product detail
   */
  getProductDetailCacheKey(masterParentId) {
    return `product_detail_v2_${masterParentId}`;
  }

  /**
   * Get cached product detail from localStorage
   */
  getCachedProductDetail(masterParentId) {
    try {
      const cacheKey = this.getProductDetailCacheKey(masterParentId);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        // Drop the pre-v2 key so a generic parent title cannot linger.
        try {
          localStorage.removeItem(`product_detail_${masterParentId}`);
        } catch (e) {
          // ignore
        }
        return null;
      }
      
      const cacheData = JSON.parse(cached);
      const now = Date.now();
      const cacheAge = now - cacheData.timestamp;
      const cacheTTL = 60 * 60 * 1000; // 1 hour in milliseconds
      
      if (cacheAge > cacheTTL) {
        // Cache expired, remove it
        localStorage.removeItem(cacheKey);
        logger.info('[ProductDetail] Cache expired for MasterParent', masterParentId);
        return null;
      }
      
      logger.info('[ProductDetail] Cache HIT for MasterParent', masterParentId, `(age: ${Math.round(cacheAge / 1000)}s)`);
      return cacheData.data;
    } catch (error) {
      logger.warn('[ProductDetail] Error reading cache:', error);
      return null;
    }
  }

  /**
   * Cache product detail in localStorage
   */
  setCachedProductDetail(masterParentId, data) {
    try {
      const cacheKey = this.getProductDetailCacheKey(masterParentId);
      const cacheData = {
        data: data,
        timestamp: Date.now()
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      logger.info('[ProductDetail] Cached product detail for MasterParent', masterParentId);
    } catch (error) {
      // Handle quota exceeded or other localStorage errors gracefully
      if (error.name === 'QuotaExceededError') {
        logger.warn('[ProductDetail] localStorage quota exceeded, clearing old cache entries');
        this.clearOldCacheEntries();
        // Try again after clearing
        try {
          const cacheKey = this.getProductDetailCacheKey(masterParentId);
          const cacheData = {
            data: data,
            timestamp: Date.now()
          };
          localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (retryError) {
          logger.warn('[ProductDetail] Failed to cache after clearing old entries:', retryError);
        }
      } else {
        logger.warn('[ProductDetail] Error caching product detail:', error);
      }
    }
  }

  /**
   * Clear old cache entries to free up space
   */
  clearOldCacheEntries() {
    try {
      const keysToRemove = [];
      const now = Date.now();
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('product_detail_')) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const cacheData = JSON.parse(cached);
              if (now - cacheData.timestamp > maxAge) {
                keysToRemove.push(key);
              }
            }
          } catch (e) {
            // Invalid cache entry, remove it
            keysToRemove.push(key);
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      if (keysToRemove.length > 0) {
        logger.info(`[ProductDetail] Cleared ${keysToRemove.length} old cache entries`);
      }
    } catch (error) {
      logger.warn('[ProductDetail] Error clearing old cache entries:', error);
    }
  }

  /**
   * Clear cache for a specific MasterParent
   */
  clearProductDetailCache(masterParentId) {
    try {
      const cacheKey = this.getProductDetailCacheKey(masterParentId);
      localStorage.removeItem(cacheKey);
      logger.info('[ProductDetail] Cleared cache for MasterParent', masterParentId);
    } catch (error) {
      logger.warn('[ProductDetail] Error clearing cache:', error);
    }
  }

  /**
   * Load product data from API
   */
  async loadProduct() {
    try {
      this.showLoading();
      
      let productData;
      
      // Check if this is a MasterParent detail page
      if (this.isMasterParent && this.masterParentId) {
        logger.info('[ProductDetail] Loading Master Parent:', this.masterParentId);
        // Ensure masterParentId is a number (API expects numeric ID)
        const parentIdNum = parseInt(this.masterParentId, 10);
        if (isNaN(parentIdNum)) {
          logger.error('[ProductDetail] Invalid Master Parent ID (not a number):', this.masterParentId);
          this.showError(`Invalid Master Parent ID: ${this.masterParentId}`);
          return;
        }
        
        // Try to get from cache first
        const cachedData = this.getCachedProductDetail(parentIdNum);
        if (cachedData) {
          productData = cachedData;
          logger.info('[ProductDetail] Using cached Master Parent data');
        } else {
          // Fetch MasterParent detail from API
          try {
            const apiUrl = `${window.apiService.baseUrl}/products/master-parents/${parentIdNum}/full-detail/`;
            logger.info('[ProductDetail] Calling API:', apiUrl);
            productData = await window.apiService.getMasterParentDetail(parentIdNum);
            logger.info('[ProductDetail] Master Parent data received:', productData);
            
            if (!productData) {
              logger.error('[ProductDetail] Master Parent data is null/undefined');
              this.showError('Master Parent not found.');
              return;
            }
            
            // Cache the response
            this.setCachedProductDetail(parentIdNum, productData);
          } catch (apiError) {
            logger.error('[ProductDetail] Error fetching Master Parent:', apiError);
            logger.error('[ProductDetail] Error details:', {
              message: apiError.message,
              status: apiError.status,
              response: apiError.response || apiError.body
            });
            
            // Show more specific error message
            if (apiError.status === 404) {
              this.showError(`Master Parent with ID ${this.masterParentId} not found.`);
            } else if (apiError.status === 403) {
              this.showError('You do not have permission to view this Master Parent.');
            } else {
              this.showError(`Failed to load Master Parent: ${apiError.message || 'Unknown error'}`);
            }
            return;
          }
        }
        
        this.productData = productData;
        this.isMasterParentView = true;
        // MasterParent views don't have votes or related listings in the same way
        this.currentUserVote = null;
        this.relatedListings = [];
        logger.info('[ProductDetail] Master Parent loaded, isMasterParentView:', this.isMasterParentView);
      } else if (this.normalizedId) {
        // Fetch by normalized_id
        const response = await window.apiService.getProductByNormalized(this.normalizedId);
        if (!response || !response.product) {
          this.showError('No products found for this normalized ID.');
          return;
        }
        productData = response.product;
        this.relatedProducts = response.related || [];
        this.hasContributed = response.has_contributed || false;
        this.productData = productData;  // Set for render
        this.isMasterParentView = false;
      } else {
        // Existing product fetch
        productData = await window.apiService.getProduct(this.productId);
        if (!productData) {
          this.showError('Product not found.');
          return;
        }
        this.productData = productData;
        this.isMasterParentView = false;
        
        // Check if this is a bot discovery product with master_parent_id
        if (productData.master_parent_id || productData.masterParentId || productData.is_bot_discovery || productData.isBotDiscovery) {
          this.masterParentId = productData.master_parent_id || productData.masterParentId;
          logger.info('[ProductDetail] Bot discovery detected, masterParentId:', this.masterParentId);
        }
      }
      
      // Initialize currentUserVote from API response (only for regular products)
      if (!this.isMasterParentView) {
        this.currentUserVote = this.productData.currentUserVote || this.productData.current_user_vote || null;
      }
      
      // Existing: load related if not normalized view or MasterParent view
      if (!this.normalizedId && !this.isMasterParentView) {
        await this.loadRelatedListings();
      }  // Else related already loaded
      
      this.renderProduct();
      
      // For Master Parent views, renderMasterParent() handles showContent() internally
      // For regular products, showContent() is called here
      if (!this.isMasterParentView) {
        this.updateMetadata();
        this.showContent();
        
        // New: Show contribution if applicable
        if (this.hasContributed && window.accountManager && window.accountManager.currentUser) {  // Assume global currentUser
          this.showContributionMessage();
        }
      }
      // Note: For Master Parent views, showContent() and updateMetadata() are called in renderMasterParent()
      
    } catch (error) {
      logger.error('Error loading product:', error);
      if (error.status === 404) {
        this.showError(this.isMasterParent ? 'Master Parent not found.' : 'Product not found.');
      } else {
        this.showError('Failed to load product. Please try again later.');
      }
    }
  }
  
  /**
   * Load related listings for price comparison
   */
  async loadRelatedListings() {
    try {
      if (!window.apiService.getRelatedListings) {
        logger.warn('getRelatedListings method not available in apiService');
        return;
      }
      
      const response = await window.apiService.getRelatedListings(this.productId);
      
      if (response && response.results) {
        this.relatedListings = response.results;
      }
    } catch (error) {
      logger.warn('Could not load related listings:', error);
      // Not a critical error, continue without related listings
    }
  }
  
  /**
   * Render product information
   */
  renderProduct() {
    try {
      const product = this.productData;
      
      if (!product) {
        logger.error('ProductDetailManager.renderProduct: No product data available');
        return;
      }
      
      logger.info('ProductDetailManager.renderProduct: Rendering product', product);
      logger.info('[ProductDetail] isMasterParentView:', this.isMasterParentView, 'isMasterParent:', this.isMasterParent);
      
      // Handle MasterParent view differently
      if (this.isMasterParentView) {
        logger.info('[ProductDetail] Routing to renderMasterParent()');
        this.renderMasterParent(product);
        return;
      }
      
      logger.info('[ProductDetail] Routing to regular product render');
      
      // Product image - support multiple images with slider
      this.renderProductImages(product);
      
      // Product name
      const productNameEl = document.getElementById('productName');
      if (productNameEl) {
        productNameEl.textContent = product.name || 'Untitled Product';
      } else {
        logger.warn('ProductDetailManager.renderProduct: productName element not found');
      }
    
      // Product metadata
      const productConditionEl = document.getElementById('productCondition');
      if (productConditionEl) {
        productConditionEl.textContent = `Condition: ${product.condition || 'N/A'}`;
      }
      
      const categoryText = product.mainCategory 
        ? `${product.mainCategory}${product.subcategory ? ' > ' + product.subcategory : ''}`
        : product.category || 'Uncategorized';
      const productCategoryEl = document.getElementById('productCategory');
      if (productCategoryEl) {
        productCategoryEl.textContent = categoryText;
      }
      
      const marketplace = this.getProductMarketplace(product);
      const marketplaceName = this.getMarketplaceName(marketplace);
      const productMarketplaceEl = document.getElementById('productMarketplace');
      if (productMarketplaceEl) {
        productMarketplaceEl.textContent = `Source: ${marketplaceName}`;
      }
      const marketplaceNameEl = document.getElementById('marketplaceName');
      if (marketplaceNameEl) {
        marketplaceNameEl.textContent = marketplaceName;
      }
      
      // Update breadcrumb
      this.updateBreadcrumb(product);
      
      // Submitter info
      if (product.submitterUsername) {
        const submitterInfoEl = document.getElementById('submitterInfo');
        if (submitterInfoEl) {
          submitterInfoEl.style.display = 'flex';
          const submitterNameEl = document.getElementById('submitterName');
          if (submitterNameEl) {
            submitterNameEl.textContent = product.submitterUsername;
          }
          
          if (product.addedAt) {
            const addedDate = new Date(product.addedAt);
            const daysAgo = Math.floor((Date.now() - addedDate) / (1000 * 60 * 60 * 24));
            const addedDateEl = document.getElementById('addedDate');
            if (addedDateEl) {
              addedDateEl.textContent = daysAgo === 0 
                ? 'Today' 
                : daysAgo === 1 
                  ? 'Yesterday' 
                  : `${daysAgo} days ago`;
            }
          }
        }
      }
      
      // Price
      const productPriceEl = document.getElementById('productPrice');
      if (productPriceEl) {
        productPriceEl.textContent = product.price || '$0.00';
      }
      
      // Retail price and discount — gated with homepage cards. Seller-declared
      // eBay originalPrice is launch MSRP, not a current-new baseline.
      if (window.UF_PUBLIC_SAVINGS_CLAIMS_ENABLED === true && product.retailPrice && product.retailPrice > 0) {
        // Convert retailPrice to number (handle string values)
        const retailPriceNum = typeof product.retailPrice === 'string' 
          ? parseFloat(String(product.retailPrice).replace(/[^0-9.]/g, ''))
          : Number(product.retailPrice);
        
        if (retailPriceNum > 0 && !isNaN(retailPriceNum)) {
          const retailPriceContainerEl = document.getElementById('retailPriceContainer');
          if (retailPriceContainerEl) {
            retailPriceContainerEl.style.display = 'flex';
            const retailPriceEl = document.getElementById('retailPrice');
            if (retailPriceEl) {
              retailPriceEl.textContent = `$${retailPriceNum.toFixed(2)}`;
            }
            
            // Calculate and show discount
            const currentPrice = parseFloat(String(product.price).replace(/[^0-9.]/g, ''));
            if (currentPrice > 0) {
              const discountPercent = Math.round(((retailPriceNum - currentPrice) / retailPriceNum) * 100);
              if (discountPercent > 0) {
                const discountBadge = document.getElementById('discountBadge');
                if (discountBadge) {
                  discountBadge.textContent = `~${discountPercent}% off!`;
                  discountBadge.style.display = 'block';
                }
              }
            }
          }
        }
      }
      
      // Early access badge
      if (product.earlyAccess) {
        const earlyAccessBadgeEl = document.getElementById('earlyAccessBadge');
        if (earlyAccessBadgeEl) {
          earlyAccessBadgeEl.style.display = 'block';
        }
      }
      
      // Buy button — same guest disclaimer path as buy-box (navigate-external-product)
      const buyBtn = document.getElementById('buyNowBtn');
      if (buyBtn) {
        const raw = product.affiliateLink || product.affiliate_link || '';
        const externalUrl = (raw && raw !== '#')
          ? raw
          : (this.getEbayItemUrlFromProductId(product.productId) || '#');
        buyBtn.href = '#';
        buyBtn.removeAttribute('target');
        buyBtn.setAttribute('data-action', 'navigate-external-product');
        buyBtn.setAttribute('data-product-url', externalUrl);
        buyBtn.setAttribute('data-product-id', String(this.productId || product.productId || ''));
        buyBtn.onclick = null;
      }
      
      // Voting
      this.renderVoting(product);

      // Watch (price drop / stock alerts)
      this.renderWatch(product);

      // Description
      if (product.description) {
        const productDescriptionSectionEl = document.getElementById('productDescriptionSection');
        if (productDescriptionSectionEl) {
          productDescriptionSectionEl.style.display = 'block';
          const productDescriptionEl = document.getElementById('productDescription');
          if (productDescriptionEl) {
            productDescriptionEl.textContent = product.description;
          }
        }
      }
      
      // Specifications
      if (product.specifications) {
        this.renderSpecifications(product.specifications);
      }
      
      // Render buy box
      this.renderBuyBox(product);
      
      // Render market pulse
      // DEPRECATED: Market Pulse graph feature temporarily disabled
      // this.renderMarketPulse(product);
      
      // Render deal quality score
      this.renderDealQualityScore(product);
      
      // Related listings (check both relatedProducts for normalized view and relatedListings for regular view)
      const hasRelatedListings = (this.relatedProducts && this.relatedProducts.length > 0) || 
                                  (this.relatedListings && this.relatedListings.length > 0);
      if (hasRelatedListings) {
        this.renderRelatedListings();
        this.renderOffersTable();
        this.updateFilterCount();
      }
      
      logger.info('ProductDetailManager.renderProduct: Product rendered successfully');
    } catch (error) {
      logger.error('ProductDetailManager.renderProduct: Error rendering product:', error);
      throw error; // Re-throw to be caught by loadProduct
    }
  }
  
  /**
   * Render voting interface
   */
  renderVoting(product) {
    const voteScore = product.voteScore || 0;
    const scoreElement = document.getElementById('voteScore');
    if (scoreElement) {
      // A visible "0" on every unvoted product makes the site feel empty —
      // show the number only once someone has actually voted.
      scoreElement.textContent = voteScore !== 0 ? (voteScore > 0 ? `+${voteScore}` : voteScore) : '';

      // Add color class
      scoreElement.className = 'vote-score-large';
      if (voteScore > 0) {
        scoreElement.classList.add('positive');
      } else if (voteScore < 0) {
        scoreElement.classList.add('negative');
      }
    }
    
    const upvoteBtn = document.getElementById('upvoteBtn');
    const downvoteBtn = document.getElementById('downvoteBtn');
    
    if (!upvoteBtn || !downvoteBtn) {
      logger.warn('Vote buttons not found in renderVoting');
      return;
    }
    
    // Check if user is logged in (check both this.currentUser and window.accountManager)
    const isLoggedIn = this.currentUser || (window.accountManager && window.accountManager.currentUser);
    
    // Enable/disable buttons based on login status
    upvoteBtn.disabled = !isLoggedIn;
    downvoteBtn.disabled = !isLoggedIn;
    
    // Remove active classes first
    upvoteBtn.classList.remove('active');
    downvoteBtn.classList.remove('active');
    
    // Get user's vote from backend API (not localStorage)
    // Backend returns 'up', 'down', or null (for guests or users who haven't voted)
    let userVote = product.currentUserVote || product.current_user_vote || null;
    
    // Update this.currentUserVote for optimistic updates during voting
    if (userVote !== undefined) {
      this.currentUserVote = userVote;
    }
    
    // Set active state based on API data
    if (userVote === 'up') {
      upvoteBtn.classList.add('active');
    } else if (userVote === 'down') {
      downvoteBtn.classList.add('active');
    }
  }

  /**
   * Render the Watch button.
   *
   * Unlike the vote buttons, this stays enabled for guests: "watch this" is the
   * moment someone has a reason to make an account, so we route to sign-in with a
   * return URL rather than showing a dead disabled control.
   */
  renderWatch(product) {
    const btn = document.getElementById('watchBtn');
    if (!btn) return;

    const parentId = this.masterParentId
      || product?.masterParentId
      || product?.master_parent_id
      || null;

    // Watches are keyed to a catalog parent; legacy products have none.
    if (!parentId) {
      btn.style.display = 'none';
      return;
    }
    btn.style.display = '';
    this.watchParentId = parentId;

    const isLoggedIn = this.currentUser || (window.accountManager && window.accountManager.currentUser);
    this.isWatched = Boolean(product?.isWatched ?? product?.is_watched ?? false);
    this._applyWatchButtonState(btn, this.isWatched, isLoggedIn);

    const maxWrap = document.getElementById('watchMaxPriceWrap');
    const maxInput = document.getElementById('watchMaxPrice');
    if (maxWrap) maxWrap.style.display = '';
    // The threshold already saved against this watch. It was read from the
    // product payload, which never carried it -- so the field always rendered
    // blank, and toggling the watch off and on wrote the blank back over it.
    const savedMax = product?.watchMaxPrice ?? product?.watch_max_price
      ?? product?.maxPrice ?? product?.max_price;
    if (maxInput && savedMax != null) maxInput.value = savedMax;

    // Changing the threshold on a watch you already have saves it. Without
    // this the input only did anything on the click that started the watch,
    // and there was no way to change your mind short of unwatching.
    if (maxInput && maxInput.dataset.thresholdBound !== 'true') {
      maxInput.dataset.thresholdBound = 'true';
      maxInput.addEventListener('change', () => this.saveWatchThreshold());
    }

    if (btn.dataset.watchBound === 'true') return;
    btn.dataset.watchBound = 'true';
    btn.addEventListener('click', () => this.toggleWatch());
  }

  _applyWatchButtonState(btn, watched, isLoggedIn) {
    const label = btn.querySelector('.watch-btn-label');
    btn.classList.toggle('is-watching', !!watched);
    btn.setAttribute('aria-pressed', watched ? 'true' : 'false');
    if (label) label.textContent = watched ? 'Watching' : 'Watch';
    btn.title = watched
      ? 'Stop getting alerts for this item'
      : (isLoggedIn
        ? 'Get alerts when the price drops or stock changes'
        : 'Sign in to get alerts when the price drops');
  }

  /**
   * Save the alert threshold on a watch that already exists.
   *
   * Only meaningful once watching -- before that the value is picked up by
   * toggleWatch when it creates the row. POST /auth/watches/ upserts on
   * (user, parent), so this is the same call.
   */
  async saveWatchThreshold() {
    const input = document.getElementById('watchMaxPrice');
    if (!input || !this.watchParentId || !this.isWatched || !window.apiService) return;
    const value = input.value.trim();
    if (value !== '' && !(Number(value) >= 0)) {
      this.showNotification('Enter a price, or leave it blank for any drop', 'error');
      return;
    }
    input.disabled = true;
    try {
      await window.apiService.watchProduct(this.watchParentId, { maxPrice: value });
      this.showNotification(
        value === '' ? "Alert cleared — we'll tell you about any drop"
          : `We'll alert you if it drops under $${value}`,
        'success',
      );
    } catch (e) {
      logger.warn('Could not save watch threshold', e);
      this.showNotification('Could not save that alert', 'error');
    } finally {
      input.disabled = false;
    }
  }

  /**
   * Toggle the watch optimistically, rolling back if the request fails.
   */
  async toggleWatch() {
    const btn = document.getElementById('watchBtn');
    if (!btn || !this.watchParentId) return;

    const isLoggedIn = this.currentUser || (window.accountManager && window.accountManager.currentUser);
    if (!isLoggedIn) {
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login.html?returnTo=${returnTo}`;
      return;
    }
    if (!window.apiService) {
      this.showNotification('Watching is unavailable right now', 'error');
      return;
    }
    if (this._watchPending) return;
    this._watchPending = true;

    const wasWatched = !!this.isWatched;
    const next = !wasWatched;
    // Optimistic: flip immediately, revert below if the server disagrees.
    this.isWatched = next;
    this._applyWatchButtonState(btn, next, true);
    btn.disabled = true;

    try {
      if (next) {
        const maxInput = document.getElementById('watchMaxPrice');
        const maxPrice = maxInput && maxInput.value !== '' ? maxInput.value : null;
        await window.apiService.watchProduct(this.watchParentId, { maxPrice });
        this.showNotification(
          maxPrice
            ? `Watching. We'll alert you if it drops under $${maxPrice}.`
            : "Watching. We'll alert you to price drops.",
          'success',
        );
      } else {
        await window.apiService.unwatchByParentId(this.watchParentId);
        this.showNotification('Stopped watching this item');
      }
    } catch (e) {
      logger.warn('Watch toggle failed', e);
      this.isWatched = wasWatched;
      this._applyWatchButtonState(btn, wasWatched, true);
      this.showNotification(
        next ? 'Could not start watching. Try again.' : 'Could not stop watching. Try again.',
        'error',
      );
    } finally {
      btn.disabled = false;
      this._watchPending = false;
    }
  }

  /**
   * Render product specifications
   */
  renderSpecifications(specs) {
    try {
      const specsContainer = document.getElementById('productSpecs');
      const specsObj = typeof specs === 'string' ? JSON.parse(specs) : specs;
      
      let html = '';
      for (const [key, value] of Object.entries(specsObj)) {
        html += `
          <div class="spec-item">
            <span class="spec-label">${this.escapeHtml(key)}</span>
            <span class="spec-value">${this.escapeHtml(String(value))}</span>
          </div>
        `;
      }
      
      specsContainer.innerHTML = html;
      document.getElementById('productSpecsSection').style.display = 'block';
    } catch (error) {
      logger.error('Error rendering specifications:', error);
    }
  }
  
  /**
   * Render related listings with price visualization
   */
  renderRelatedListings() {
    const section = document.getElementById('relatedListingsSection');
    if (!section) return;
    section.style.display = 'block';
    
    // Use relatedProducts if available (normalized view), otherwise relatedListings
    const relatedListings = this.relatedProducts || this.relatedListings || [];
    
    // Ensure productData has productId field
    const currentProduct = {
      ...this.productData,
      productId: this.productData.productId || this.productId || this.productData.id
    };
    
    // Combine current product with related listings
    const allListings = [currentProduct, ...relatedListings];
    
    // Render price visualization
    this.renderPriceVisualization(allListings);
    
    // Render listings grid
    const grid = document.getElementById('relatedListingsGrid');
    if (grid) {
      if (consoleOn()) {
        // Every other listing for this model, as the tile the rest of the site
        // uses. They link to eBay rather than into the catalog: these are the
        // *offers* on this product's page, not other products.
        const cards = allListings
          .map((listing) => {
            const image = this.getHighResImageUrl(
              listing.imageUrl || listing.image_url || ''
            );
            if (!/^https?:\/\//.test(String(image))) return null;
            const href = listing.affiliateLink || listing.affiliate_link
              || this.getEbayItemUrlFromProductId(listing.productId || listing.product_id);
            return toCard(listing, {
              url: href && href !== '#' ? href : '#',
              imageUrl: image,
              external: true,
            });
          })
          .filter(Boolean);
        grid.innerHTML = consoleGrid(cards);
      } else {
        grid.innerHTML = allListings.map(listing => this.renderListingCard(listing)).join('');
      }
    }
  }
  
  /**
   * The Console's market ladder: one row per listing, ordered by price, with
   * the 30-day median *asking* price marked among them.
   *
   * It replaces the legacy scatter chart rather than sitting beside it. Both
   * describe the same numbers, and the ladder can say what each row's seller
   * looks like, which is the question this page exists to answer.
   */
  renderMarketLadder(container, listings) {
    const currentId = this.productData?.productId || this.productId || this.productData?.id;
    const rows = listings.map((listing) => {
      const score = sellerTrust(listing);
      return {
        price: this.parsePrice(listing.price),
        mine: (listing.productId || listing.id) === currentId,
        tier: score ? score.tier : null,
        seller: [
          score ? `${listing.sellerFeedbackPct ?? listing.seller_feedback_pct}%` : 'seller not rated',
          score ? `${Number(listing.sellerSalesCount ?? listing.seller_sales_count).toLocaleString()} reviews` : '',
          (listing.productId || listing.id) === currentId ? 'this listing' : '',
        ].filter(Boolean).join(' · '),
      };
    });

    // The typical ask, when a MarketPriceStat covers this model. Never a sold
    // price -- see console-product.js.
    const context = this.productData?.priceContext || this.productData?.price_context;
    const median = context ? parseFloat(context.median) : NaN;

    const html = ladderHtml(rows, Number.isFinite(median) ? median : null);
    if (html) container.innerHTML = html;
  }

  /**
   * Render price visualization chart
   */
  renderPriceVisualization(listings) {
    // Validate DOM elements exist
    const chartContainer = document.getElementById('priceChart');
    const minPriceEl = document.getElementById('minPrice');
    const avgPriceEl = document.getElementById('avgPrice');
    const maxPriceEl = document.getElementById('maxPrice');
    
    if (!chartContainer) {
      logger.warn('Price visualization elements not found in DOM');
      return;
    }

    if (consoleOn()) {
      this.renderMarketLadder(chartContainer, listings);
      return;
    }

    if (!minPriceEl || !avgPriceEl || !maxPriceEl) {
      logger.warn('Price visualization elements not found in DOM');
      return;
    }
    
    // Parse and validate prices
    const listingsWithPrices = listings.map(listing => {
      const price = this.parsePrice(listing.price);
      if (price === null) {
        logger.warn('Invalid or missing price for listing:', listing);
        return null;
      }
      return { ...listing, parsedPrice: price };
    }).filter(item => item !== null);
    
    if (listingsWithPrices.length === 0) {
      logger.warn('No valid prices found for price visualization');
      return;
    }
    
    // Extract valid prices
    const prices = listingsWithPrices.map(l => l.parsedPrice);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    const priceRange = maxPrice - minPrice;
    
    // Update price stats
    minPriceEl.textContent = `$${minPrice.toFixed(2)}`;
    avgPriceEl.textContent = `$${avgPrice.toFixed(2)}`;
    maxPriceEl.textContent = `$${maxPrice.toFixed(2)}`;
    
    // Identify current product ID (handle normalized view where this.productId may be null)
    const currentProductId = this.productData?.productId || this.productId || this.productData?.id;
    
    // Group listings by price to handle duplicates
    const priceGroups = {};
    listingsWithPrices.forEach(listing => {
      const price = listing.parsedPrice;
      if (!priceGroups[price]) {
        priceGroups[price] = [];
      }
      priceGroups[price].push(listing);
    });
    
    // Render price chart with handling for duplicate prices
    let chartHTML = '';
    listingsWithPrices.forEach(listing => {
      const price = listing.parsedPrice;
      const listingId = listing.productId || listing.id;
      
      // Calculate horizontal position
      const percentage = priceRange > 0 ? ((price - minPrice) / priceRange) * 100 : 50;
      
      // Check if current product (handle both productId and id fields)
      const isCurrentProduct = (listing.productId || listing.id) === currentProductId;
      
      // Handle duplicate prices with vertical offset
      const priceGroup = priceGroups[price];
      const indexInGroup = priceGroup.findIndex(l => (l.productId || l.id) === listingId);
      const hasDuplicates = priceGroup.length > 1;
      
      // Apply vertical offset for duplicates (stagger top/bottom)
      let verticalStyle = '';
      if (hasDuplicates) {
        // Alternate between top and bottom positions
        const isEven = indexInGroup % 2 === 0;
        const offset = Math.floor(indexInGroup / 2) * 20; // 20px offset per pair
        if (isEven) {
          verticalStyle = `top: ${10 + offset}px;`;
        } else {
          verticalStyle = `bottom: ${10 + offset}px;`;
        }
      }
      
      chartHTML += `
        <div class="price-bar ${isCurrentProduct ? 'current' : ''}" 
             style="left: ${percentage}%; ${verticalStyle}" 
             data-product-id="${this.escapeHtml(listingId)}">
          <div class="price-marker" title="${this.escapeHtml(listing.name)} - ${this.escapeHtml(listing.price)}">
            <span class="price-label">${this.escapeHtml(listing.price)}</span>
            <span class="condition-label">${this.escapeHtml(listing.condition || 'N/A')}</span>
          </div>
        </div>
      `;
    });
    
    chartContainer.innerHTML = chartHTML;
    
    // Add click handlers to price bars
    chartContainer.querySelectorAll('.price-bar').forEach(bar => {
      bar.addEventListener('click', (e) => {
        const productId = e.currentTarget.dataset.productId;
        const listing = listingsWithPrices.find(l => (l.productId || l.id) === productId);
        if (listing) {
          this.scrollToListing(productId);
        }
      });
    });
  }
  
  /**
   * Render a listing card
   */
  renderListingCard(listing) {
    const currentProductId = this.productData?.productId || this.productId || this.productData?.id;
    const listingId = listing.productId || listing.id;
    const isCurrentProduct = listingId === currentProductId;
    const marketplace = this.getProductMarketplace(listing);
    const marketplaceName = this.getMarketplaceName(marketplace);
    const listingImgRaw = listing.imageUrl || listing.image_url || this.getPlaceholderImage();
    const listingImgHighRes = this.getHighResImageUrl(listingImgRaw);
    const useHighResListing = listingImgHighRes !== listingImgRaw && listingImgRaw.startsWith && listingImgRaw.startsWith('http');
    const listingImgSrc = useHighResListing ? listingImgHighRes : listingImgRaw;
    const listingImgFallback = useHighResListing ? listingImgRaw : '';
    const listingImgOnError = useHighResListing
      ? 'if(this.dataset.fallbackSrc){this.src=this.dataset.fallbackSrc;this.removeAttribute(\'data-fallback-src\');}else{this.src=window.productDetailManager?.getPlaceholderImage?.()||this.src;}'
      : 'this.src=window.productDetailManager?.getPlaceholderImage?.()||this.src;';
    const listingUrl = (listing.affiliateLink || listing.affiliate_link) && (listing.affiliateLink || listing.affiliate_link) !== '#' ? (listing.affiliateLink || listing.affiliate_link) : (this.getEbayItemUrlFromProductId(listing.productId || listing.product_id) || '#');
    
    return `
      <div class="listing-card ${isCurrentProduct ? 'current-listing' : ''}" data-listing-id="${this.escapeHtml(listingId)}">
        ${isCurrentProduct ? '<div class="current-badge">Current Listing</div>' : ''}
        <div class="listing-image">
          <img src="${this.escapeHtml(listingImgSrc)}" ${listingImgFallback ? `data-fallback-src="${this.escapeHtml(listingImgFallback)}"` : ''} alt="${this.escapeHtml(listing.name)}" onerror="${listingImgOnError}">
        </div>
        <div class="listing-info">
          <div class="listing-marketplace">${this.escapeHtml(marketplaceName)}</div>
          <div class="listing-condition">${this.escapeHtml(listing.condition || 'N/A')}</div>
          <div class="listing-price">${this.escapeHtml(listing.price)}${listing.currency ? ' ' + this.escapeHtml(listing.currency) : ''}</div>
          <div class="listing-votes">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
              <path d="M12 19V5M5 12l7-7 7 7"/>
            </svg>
            <span>${listing.voteScore || 0}</span>
          </div>
        </div>
        <a href="${this.escapeHtml(listingUrl)}" target="_blank" rel="noopener noreferrer" class="listing-buy-btn" data-action="navigate-external-product" data-product-url="${this.escapeHtml(listingUrl)}" data-listing-id="${this.escapeHtml(listingId)}">
          View on ${this.escapeHtml(marketplaceName)}
        </a>
      </div>
    `;
  }
  
  /**
   * Scroll to specific listing
   */
  scrollToListing(productId) {
    const listingCard = document.querySelector(`[data-listing-id="${productId}"]`);
    if (listingCard) {
      listingCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
      listingCard.classList.add('highlight');
      setTimeout(() => listingCard.classList.remove('highlight'), 2000);
    }
  }
  
  /**
   * Initialize event listeners
   */
  initEventListeners() {
    // Voting buttons - with error handling
    const upvoteBtn = document.getElementById('upvoteBtn');
    const downvoteBtn = document.getElementById('downvoteBtn');
    
    if (upvoteBtn) {
      upvoteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleVote('up');
      });
      // Ensure button is clickable
      upvoteBtn.style.pointerEvents = 'auto';
      upvoteBtn.style.cursor = 'pointer';
    } else {
      logger.error('upvoteBtn not found when initializing event listeners');
    }
    
    if (downvoteBtn) {
      downvoteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.handleVote('down');
      });
      // Ensure button is clickable
      downvoteBtn.style.pointerEvents = 'auto';
      downvoteBtn.style.cursor = 'pointer';
    } else {
      logger.error('downvoteBtn not found when initializing event listeners');
    }
    
    // Report button
    const reportBtn = document.getElementById('reportBtn');
    if (reportBtn) {
      reportBtn.addEventListener('click', () => this.handleReport());
    }
    
    // Social sharing
    const shareFacebook = document.getElementById('shareFacebook');
    if (shareFacebook) {
      shareFacebook.addEventListener('click', () => this.handleShare('facebook'));
    }
    
    const shareTwitter = document.getElementById('shareTwitter');
    if (shareTwitter) {
      shareTwitter.addEventListener('click', () => this.handleShare('twitter'));
    }
    
    const shareEmail = document.getElementById('shareEmail');
    if (shareEmail) {
      shareEmail.addEventListener('click', () => this.handleShare('email'));
    }
    
    const shareCopy = document.getElementById('shareCopy');
    if (shareCopy) {
      shareCopy.addEventListener('click', () => this.handleShare('copy'));
    }
    
    // Native share (if available)
    if (navigator.share) {
      const nativeShareBtn = document.getElementById('shareNative');
      if (nativeShareBtn) {
        nativeShareBtn.style.display = 'block';
        nativeShareBtn.addEventListener('click', () => this.handleShare('native'));
      }
    }
    
    // Condition filter pills
    const filterPills = document.querySelectorAll('.condition-filter-pill');
    filterPills.forEach(pill => {
      pill.addEventListener('click', (e) => {
        const condition = e.currentTarget.dataset.condition;
        this.filterByCondition(condition);
      });
    });
    
    // Sort buttons
    const sortBestPrice = document.getElementById('sortBestPrice');
    const sortMostRecent = document.getElementById('sortMostRecent');
    if (sortBestPrice) {
      sortBestPrice.addEventListener('click', () => this.setSortMode('best-price'));
    }
    if (sortMostRecent) {
      sortMostRecent.addEventListener('click', () => this.setSortMode('most-recent'));
    }
    
    // Load filter from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilter = urlParams.get('condition');
    if (urlFilter) {
      this.filterByCondition(urlFilter, false); // false = don't update URL
    }
  }
  
  /**
   * Filter listings by condition
   */
  filterByCondition(condition, updateURL = true) {
    this.activeConditionFilter = condition;
    
    // Update filter pill active states
    const filterPills = document.querySelectorAll('.condition-filter-pill');
    filterPills.forEach(pill => {
      if (pill.dataset.condition === condition) {
        pill.classList.add('active');
      } else {
        pill.classList.remove('active');
      }
    });
    
    // Update URL if requested
    if (updateURL) {
      const url = new URL(window.location);
      if (condition === 'all') {
        url.searchParams.delete('condition');
      } else {
        url.searchParams.set('condition', condition);
      }
      window.history.pushState({}, '', url);
    }
    
    // Re-render affected components
    this.renderOffersTable();
    // DEPRECATED: Market Pulse graph feature temporarily disabled
    // this.renderMarketPulse(this.productData);
    this.renderBuyBox(this.productData);
    
    // Update filter count
    this.updateFilterCount();
  }
  
  /**
   * Update filter count display
   */
  updateFilterCount() {
    const filterCountEl = document.getElementById('filterCount');
    if (!filterCountEl) return;
    
    // Get all listings
    const allListings = [];
    if (this.productData) {
      allListings.push(this.productData);
    }
    const relatedListings = this.relatedProducts || this.relatedListings || [];
    allListings.push(...relatedListings);
    
    // Count filtered listings
    const filtered = this.getFilteredListings(allListings);
    const total = allListings.length;
    
    if (this.activeConditionFilter === 'all' || filtered.length === total) {
      filterCountEl.style.display = 'none';
    } else {
      filterCountEl.textContent = `Showing ${filtered.length} of ${total}`;
      filterCountEl.style.display = 'inline';
    }
  }
  
  /**
   * Get filtered listings based on active condition filter
   */
  getFilteredListings(listings) {
    if (this.activeConditionFilter === 'all') {
      return listings;
    }
    return listings.filter(listing => {
      const condition = (listing.condition || '').trim();
      return condition === this.activeConditionFilter;
    });
  }
  
  /**
   * Set sort mode and re-render table
   */
  setSortMode(sortMode) {
    this.activeSortMode = sortMode;
    
    // Update button active states
    const sortBestPrice = document.getElementById('sortBestPrice');
    const sortMostRecent = document.getElementById('sortMostRecent');
    if (sortBestPrice && sortMostRecent) {
      if (sortMode === 'best-price') {
        sortBestPrice.classList.add('active');
        sortBestPrice.style.background = '#6366f1';
        sortBestPrice.style.color = 'white';
        sortMostRecent.classList.remove('active');
        sortMostRecent.style.background = '#e5e7eb';
        sortMostRecent.style.color = '#374151';
      } else {
        sortMostRecent.classList.add('active');
        sortMostRecent.style.background = '#6366f1';
        sortMostRecent.style.color = 'white';
        sortBestPrice.classList.remove('active');
        sortBestPrice.style.background = '#e5e7eb';
        sortBestPrice.style.color = '#374151';
      }
    }
    
    // Re-render table with new sort
    this.renderOffersTable();
  }
  
  /**
   * Sort listings based on active sort mode
   */
  sortListings(listings) {
    const sorted = [...listings]; // Create copy to avoid mutating original
    
    if (this.activeSortMode === 'best-price') {
      // Sort by: 1) Active status (active first), 2) Price (low to high)
      sorted.sort((a, b) => {
        const isActiveA = a.isActive !== undefined ? a.isActive : Boolean(a.affiliateLink);
        const isActiveB = b.isActive !== undefined ? b.isActive : Boolean(b.affiliateLink);
        
        // Active listings first
        if (isActiveA !== isActiveB) {
          return isActiveB ? 1 : -1; // Active (true) comes before inactive (false)
        }
        
        // Then by price (low to high)
        const priceA = this.parsePrice(a.price);
        const priceB = this.parsePrice(b.price);
        if (priceA === null) return 1;
        if (priceB === null) return -1;
        return priceA - priceB;
      });
    } else {
      // Sort by: 1) Active status (active first), 2) Date (most recent first)
      sorted.sort((a, b) => {
        const isActiveA = a.isActive !== undefined ? a.isActive : Boolean(a.affiliateLink);
        const isActiveB = b.isActive !== undefined ? b.isActive : Boolean(b.affiliateLink);
        
        // Active listings first
        if (isActiveA !== isActiveB) {
          return isActiveB ? 1 : -1; // Active (true) comes before inactive (false)
        }
        
        // Then by date (most recent first)
        const dateA = new Date(a.updatedAt || a.updated_at || a.createdAt || a.created_at || 0);
        const dateB = new Date(b.updatedAt || b.updated_at || b.createdAt || b.created_at || 0);
        return dateB - dateA; // Most recent first
      });
    }
    
    return sorted;
  }
  
  /**
   * Handle voting
   */
  async handleVote(voteType) {
    // On full page load, token/user may not be set yet; try to establish session first
    if (!this.currentUser && window.apiService) {
      try {
        if (typeof window.apiService.checkAuth === 'function') {
          const ok = await window.apiService.checkAuth();
          if (!ok) {
            this.showNotification('Please log in to vote', 'error');
            setTimeout(() => window.location.href = 'login.html', 1500);
            return;
          }
        }
        this.currentUser = (window.accountManager && window.accountManager.currentUser) || null;
        if (!this.currentUser && typeof window.apiService.getCurrentUser === 'function') {
          const userData = await window.apiService.getCurrentUser();
          this.currentUser = (userData && userData.id) ? userData : null;
          if (window.accountManager && this.currentUser) {
            window.accountManager.currentUser = this.currentUser;
          }
        }
        if (this.currentUser && this.productData) {
          this.renderVoting(this.productData);
        }
      } catch (e) {
        // ignore
      }
    }
    if (!this.currentUser) {
      this.showNotification('Please log in to vote', 'error');
      setTimeout(() => window.location.href = 'login.html', 1500);
      return;
    }
    
    const upvoteBtn = document.getElementById('upvoteBtn');
    const downvoteBtn = document.getElementById('downvoteBtn');
    
    // Check current vote state from stored state (most reliable)
    const currentVote = this.currentUserVote;
    const isUpvoted = currentVote === 'up';
    const isDownvoted = currentVote === 'down';
    
    try {
      // If clicking the same vote button that's already active, remove the vote
      if ((voteType === 'up' && isUpvoted) || (voteType === 'down' && isDownvoted)) {
        // Try to remove vote - 404 is OK (vote might not exist on backend)
        // apiService.removeVote() handles 404s gracefully and returns success
        await window.apiService.removeVote(this.productId);
        
        // Update UI immediately
        if (upvoteBtn) upvoteBtn.classList.remove('active');
        if (downvoteBtn) downvoteBtn.classList.remove('active');
      } else {
        // Add or change vote
        const response = await window.apiService.voteProduct(this.productId, voteType);
        
        // Update UI immediately
        if (upvoteBtn) {
          upvoteBtn.classList.toggle('active', voteType === 'up');
          if (voteType === 'up') {
            downvoteBtn?.classList.remove('active');
          }
        }
        if (downvoteBtn) {
          downvoteBtn.classList.toggle('active', voteType === 'down');
          if (voteType === 'down') {
            upvoteBtn?.classList.remove('active');
          }
        }
        
        // Show badge notification if any
        if (response && response.newlyAwardedBadges && response.newlyAwardedBadges.length > 0) {
          this.showBadgeNotification(response.newlyAwardedBadges);
        }
      }
      
      // Update stored vote state immediately (optimistic update)
      if ((voteType === 'up' && isUpvoted) || (voteType === 'down' && isDownvoted)) {
        // Vote was removed
        this.currentUserVote = null;
      } else {
        // Vote was added/changed
        this.currentUserVote = voteType;
      }
      
      // Reload product to get updated vote score and sync state from backend
      try {
        const updatedProduct = await window.apiService.getProduct(this.productId);
        this.productData = updatedProduct;
        // Update currentUserVote from API response
        this.currentUserVote = updatedProduct.currentUserVote || updatedProduct.current_user_vote || null;
        this.renderVoting(updatedProduct);
      } catch (error) {
        logger.warn('Could not reload product after vote:', error);
        // Non-critical, continue
      }

      // Refresh sidebar so user's vote count updates in real time (force refresh bypasses cooldown)
      if (typeof window.updateSidebarProfile === 'function') {
        window.updateSidebarProfile(0, { forceRefresh: true });
      }
      
    } catch (error) {
      logger.error('Error voting:', error);
      const errorMessage = error.message || String(error);
      // Don't show error for 404s (vote not found) - that's expected
      if (!errorMessage.includes('404') && !errorMessage.includes('Not found')) {
        this.showNotification('Failed to vote. Please try again.', 'error');
      }
    }
  }
  
  /**
   * Handle report
   */
  handleReport() {
    if (window.reportManager) {
      window.reportManager.openReportModal(this.productId);
    } else {
      this.showNotification('Report feature is unavailable', 'error');
    }
  }
  
  /**
   * Handle social sharing
   */
  handleShare(platform) {
    const productUrl = window.location.href;
    const productName = this.productData.name;
    const productPrice = this.productData.price;
    const shareText = `Check out this deal: ${productName} - ${productPrice} on UsedFindr`;
    
    if (window.socialShareManager) {
      const productForSharing = {
        ...this.productData,
        url: productUrl
      };
      
      switch (platform) {
        case 'native':
          window.socialShareManager.shareNative(productForSharing);
          break;
        case 'facebook':
          window.socialShareManager.shareFacebook(productForSharing);
          break;
        case 'twitter':
          window.socialShareManager.shareTwitter(productForSharing);
          break;
        case 'email':
          window.socialShareManager.shareEmail(productForSharing);
          break;
        case 'copy':
          window.socialShareManager.copyLink(productForSharing);
          break;
      }
    } else {
      // Fallback for copy link
      if (platform === 'copy') {
        navigator.clipboard.writeText(productUrl).then(() => {
          this.showNotification('Link copied to clipboard', 'success');
        });
      }
    }
  }
  
  /**
   * Track product click for analytics
   */
  trackProductClick() {
    if (typeof trackProductClick === 'function') {
      trackProductClick(this.productId);
    }
  }
  
  /**
   * Update breadcrumb navigation
   */
  updateBreadcrumb(product) {
    const categoryElement = document.getElementById('breadcrumb-category');
    const productElement = document.getElementById('breadcrumb-product');
    const sep = document.getElementById('breadcrumb-sep-product');
    
    const categoryText = product.mainCategory || product.category || 'Products';
    categoryElement.textContent = categoryText;
    
    // Truncate product name if too long
    const productName = product.name || 'Product';
    productElement.textContent = productName.length > 40 
      ? productName.substring(0, 40) + '...' 
      : productName;
    
    // The slash is written here rather than shipped in the markup: hidden by
    // CSS it is still page text, and a crawler read it as a dangling separator.
    sep.textContent = '/';
    sep.style.display = 'inline';
  }
  
  /**
   * Update page metadata for SEO.
   * Null-safe: Django base template may not include id="page-title" or ids on meta/link elements.
   */
  updateMetadata() {
    const product = this.productData;
    const productName = product.name || 'Product';
    const productCondition = product.condition || '';
    const productPrice = product.price || '';
    
    const setAttr = (id, attr, value) => {
      const el = document.getElementById(id);
      if (el) el.setAttribute(attr, value);
    };
    
    // Page title (document.title always works; element with id may be missing in Django template)
    // Drop empty segments rather than interpolating them: an out-of-stock parent
    // has neither a condition nor a price, and the naive template rendered
    // "Name - - | UsedFindr" on exactly the pages we now keep indexed.
    const title = [productName, productCondition, productPrice]
      .filter(Boolean)
      .join(' - ') + ' | UsedFindr';
    document.title = title;
    const titleEl = document.getElementById('page-title');
    if (titleEl) titleEl.textContent = title;

    // Description — same reason: skip the clauses we have no value for instead
    // of emitting "in  condition for  on UsedFindr".
    let description;
    if (product.description) {
      description = product.description.substring(0, 160);
    } else {
      const qualifiers = [
        productCondition ? `in ${productCondition} condition` : '',
        productPrice ? `for ${productPrice}` : '',
      ].filter(Boolean).join(' ');
      description = `Find ${productName} ${qualifiers ? qualifiers + ' ' : ''}on UsedFindr. `
        + 'Compare prices across multiple sellers.';
    }
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Open Graph (ids optional; present in root product.html, not in Django base)
    setAttr('og-title', 'content', title);
    setAttr('og-description', 'content', description);
    if (product.imageUrl) setAttr('og-image', 'content', product.imageUrl);
    
    // Twitter
    setAttr('twitter-title', 'content', title);
    setAttr('twitter-description', 'content', description);
    if (product.imageUrl) setAttr('twitter-image', 'content', product.imageUrl);
    
    // Canonical URL — the server-rendered canonical (Django template) is
    // authoritative; only derive one client-side when SSR didn't provide it,
    // so the URL shape lives in exactly one backend helper.
    const ssrCanonical = document.querySelector('link[rel="canonical"]');
    const ssrHref = ssrCanonical ? ssrCanonical.getAttribute('href') : '';
    const ssrIsProductUrl = Boolean(ssrHref) && (ssrHref.includes('/p/') || ssrHref.includes('id='));
    let canonicalUrl = ssrIsProductUrl ? ssrHref : '';
    if (!canonicalUrl) {
      let productIdForUrl = this.productId;
      if (this.isMasterParentView && this.masterParentId) {
        productIdForUrl = `master_parent_${this.masterParentId}`;
      }
      const CANONICAL_SITE_ORIGIN = 'https://usedfindr.com';
      canonicalUrl = `${CANONICAL_SITE_ORIGIN}/product.html?id=${productIdForUrl}${product.productUrlSlug ? '&slug=' + product.productUrlSlug : ''}`;
    }
    setAttr('canonical-url', 'href', canonicalUrl);
    setAttr('og-url', 'content', canonicalUrl);
    setAttr('twitter-url', 'content', canonicalUrl);
    
    this.addStructuredData(product);
  }
  
  /**
   * Add structured data for SEO
   */
  addStructuredData(product) {
    // SSR JSON-LD on master_parent catalog pages is authoritative for crawlers.
    const existing = document.getElementById('product-structured-data');
    if (existing && existing.textContent && existing.textContent.trim()) {
      return;
    }

    const structuredData = {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.name,
      "image": product.imageUrl || '',
      "description": product.description || `${product.name}${product.condition ? ` in ${product.condition} condition` : ''}`,
      "offers": {
        "@type": "Offer",
        "url": window.location.href,
        "priceCurrency": "USD",
        "price": product.price ? String(product.price).replace(/[^0-9.]/g, '') : '0',
        "itemCondition": product.condition ? `https://schema.org/${product.condition.replace(/\s+/g, '')}Condition` : "https://schema.org/NewCondition",
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": this.getMarketplaceName(this.getProductMarketplace(product))
        }
      }
    };
    
    // Never emit aggregateRating/review here: vote counts are not product
    // reviews, and fabricated rating markup risks a GSC manual action.

    // Add or update structured data script
    let script = document.getElementById('product-structured-data');
    if (!script) {
      script = document.createElement('script');
      script.id = 'product-structured-data';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }
  
  /**
   * UI state management
   */
  /**
   * Fill a state host from its <template> the first time it is shown.
   *
   * The skeleton and the "Product Not Found" block live in templates so they
   * are not part of the page's extracted text — a crawler that does not run JS
   * used to read the error copy as the product page's content. Cloning is
   * idempotent; the host keeps its contents once populated.
   */
  hydrateStateHost(host) {
    if (!host || host.childElementCount > 0) return;
    const templateId = host.dataset ? host.dataset.template : null;
    const template = templateId ? document.getElementById(templateId) : null;
    if (template && template.content) {
      host.appendChild(template.content.cloneNode(true));
    }
  }

  showLoading() {
    // The page now ships a server-rendered buy box, so replacing it with a
    // skeleton would be a downgrade. Only show the skeleton when there is no
    // server-rendered content to leave on screen.
    const hasServerRendered = Boolean(
      this.productContent && this.productContent.querySelector('[data-ssr-offer]')
    );
    if (hasServerRendered) {
      this.loadingState.style.display = 'none';
      this.errorState.style.display = 'none';
      this.productContent.style.display = 'block';
      return;
    }
    this.hydrateStateHost(this.loadingState);
    this.loadingState.style.display = 'block';
    this.errorState.style.display = 'none';
    this.productContent.style.display = 'none';
  }

  showError(message) {
    logger.error('[ProductDetail] showError() called with message:', message);
    this.hydrateStateHost(this.errorState);
    const errorMessageEl = document.getElementById('errorMessage');
    // The card's own line already says the listing is not here and why that is
    // usually not an error. A message that only restates the headline replaces
    // an explanation with a repetition, so those are dropped; anything with
    // real information in it still wins.
    const restatesTheHeadline = /^(product|master parent)\s+not\s+found\.?$/i
      .test(String(message == null ? '' : message).trim());
    if (errorMessageEl) {
      if (!restatesTheHeadline) errorMessageEl.textContent = message;
    } else {
      logger.error('[ProductDetail] errorMessage element not found in DOM');
    }

    // The pane is context for a product, and there is no product. Left alone it
    // kept drawing a "Listing" anchor pointing at nothing and an empty seller
    // card beside a card saying the listing is gone. Emptied rather than
    // hidden: console/shell.css keys the three-column grid off
    // `.pane:not(:empty)`, so a truly empty pane collapses the column, while
    // `display: none` would leave the 272px track in place.
    const pane = document.querySelector('.pane');
    if (pane) pane.textContent = '';
    // Same for the trail: it ended in an empty crumb after "Search".
    const crumbSep = document.getElementById('breadcrumb-sep-product');
    if (crumbSep) crumbSep.style.display = 'none';

    if (this.loadingState) {
      this.loadingState.style.display = 'none';
    }
    if (this.errorState) {
      this.errorState.style.display = 'block';
      logger.info('[ProductDetail] Error state displayed');
    } else {
      logger.error('[ProductDetail] errorState element not found in DOM');
    }
    if (this.productContent) {
      this.productContent.style.display = 'none';
    }
  }
  
  showContent() {
    this.loadingState.style.display = 'none';
    this.errorState.style.display = 'none';
    this.productContent.style.display = 'block';
  }
  
  /**
   * Show notification
   */
  showNotification(message, type = 'info') {
    if (window.accountManager && window.accountManager.showNotification) {
      window.accountManager.showNotification(message, type);
    } else {
      alert(message);
    }
  }
  
  /**
   * Show badge notification
   */
  showBadgeNotification(badges) {
    if (window.accountManager && window.accountManager.showBadgeNotification) {
      badges.forEach(badge => {
        window.accountManager.showBadgeNotification(badge);
      });
    }
  }

  showContributionMessage() {
    let messageEl = document.getElementById('contribution-message');
    if (!messageEl) {
      messageEl = document.createElement('div');
      messageEl.id = 'contribution-message';
      document.querySelector('.product-details').prepend(messageEl);
    }
    messageEl.innerHTML = '<div class="alert alert-success"><p class="contribution-badge">You contributed to this normalized product.</p></div>';
    messageEl.style.display = 'block';
  }
  
  /**
   * Helper: Get marketplace display name
   */
  getMarketplaceName(marketplace) {
    if (!marketplace) return 'eBay'; // Default to eBay
    
    // Normalize to lowercase for comparison
    const normalized = String(marketplace).toLowerCase().trim();
    
    const names = {
      'amazon': 'Amazon',
      'ebay': 'eBay',
      'best buy': 'Best Buy',
      'bestbuy': 'Best Buy',
      'walmart': 'Walmart',
      'target': 'Target',
      'newegg': 'Newegg',
      'etsy': 'Etsy',
      'mercari': 'Mercari',
      'facebook marketplace': 'Facebook Marketplace',
      'facebook': 'Facebook Marketplace',
      'offerup': 'OfferUp',
      'craigslist': 'Craigslist',
      'other': 'eBay' // Never show "Other" - default to eBay
    };
    
    // Never show 'Other' - default to eBay if unknown
    return names[normalized] || 'eBay';
  }
  
  /**
   * If productId is ebay_<id>, return https://www.ebay.com/itm/<id>. Otherwise return null.
   * Used so Buy on eBay always has a link when backend sends no affiliate_link yet.
   */
  getEbayItemUrlFromProductId(productId) {
    if (!productId || typeof productId !== 'string') return null;
    const match = String(productId).match(/^ebay_(.+)$/);
    if (!match) return null;
    const id = match[1].split('|')[0].trim();
    return id ? `https://www.ebay.com/itm/${id}` : null;
  }
  
  /**
   * Helper: Hours since listing data was fetched/updated (for eBay freshness disclosure).
   * Uses fetched_at first, then updated_at. Returns null if no date or parse error.
   */
  getListingAgeHours(listing) {
    if (!listing) return null;
    const dateStr = listing.fetched_at || listing.fetchedAt || listing.updated_at || listing.updatedAt;
    if (!dateStr) return null;
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return null;
      return (Date.now() - date.getTime()) / (1000 * 60 * 60);
    } catch (e) {
      return null;
    }
  }
  
  /**
   * Helper: Format listing age for display ("X hours ago" or "X days ago").
   * Returns null if age is null or < 1 hour.
   */
  formatListingAgeHours(ageHours) {
    if (ageHours == null || ageHours < 0) return null;
    if (ageHours < 1) return null;
    if (ageHours < 24) return `${Math.round(ageHours)} hour${Math.round(ageHours) !== 1 ? 's' : ''} ago`;
    const days = Math.floor(ageHours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }
  
  /**
   * Helper: Extract marketplace from product object (handles both camelCase and snake_case)
   * Defaults to 'ebay' when marketplace cannot be determined.
   */
  getProductMarketplace(product) {
    if (!product) return 'ebay';
    
    // PRIORITY 1: Check affiliate link first (most reliable; support both camelCase and snake_case from API)
    const affiliateLink = product.affiliateLink || product.affiliate_link;
    if (affiliateLink) {
      const link = String(affiliateLink).toLowerCase();
      if (link.includes('amazon.com') || link.includes('amzn.to') || link.includes('amazon.')) {
        return 'amazon';
      } else if (link.includes('ebay.com') || link.includes('ebay.us') || link.includes('ebay.')) {
        return 'ebay';
      }
    }
    
    // PRIORITY 2: Try camelCase (from toCamelCase conversion)
    let marketplace = product.sourceMarketplace;
    
    // PRIORITY 3: Fallback to snake_case (direct from API)
    if (!marketplace) {
      marketplace = product.source_marketplace;
    }
    
    // Normalize marketplace value
    if (marketplace) {
      marketplace = String(marketplace).toLowerCase().trim();
      // Never use 'other' - default to eBay
      if (marketplace === 'other') {
        return 'ebay';
      }
    }
    
    // Default to 'ebay' for all cases
    return marketplace || 'ebay';
  }
  
  /**
   * Get category-based stock image for Amazon products
   */
  getCategoryStockImage(category) {
    if (!category) return null;
    
    // Normalize category name
    const normalizedCategory = String(category).toLowerCase().trim();
    
    // Map categories to stock images
    const categoryMap = {
      'electronics': 'electronics',
      'electronic': 'electronics',
      'clothing': 'clothing',
      'apparel': 'clothing',
      'home & kitchen': 'home-kitchen',
      'home': 'home-kitchen',
      'kitchen': 'home-kitchen',
      'books': 'books',
      'book': 'books',
      'sports & outdoors': 'sports-outdoors',
      'sports': 'sports-outdoors',
      'outdoors': 'sports-outdoors',
      'toys & games': 'toys-games',
      'toys': 'toys-games',
      'games': 'toys-games',
      'automotive': 'automotive',
      'beauty & personal care': 'beauty',
      'beauty': 'beauty',
      'health & household': 'health',
      'health': 'health',
      'pet supplies': 'pets',
      'pets': 'pets',
      'tools & home improvement': 'tools',
      'tools': 'tools',
      'garden & outdoor': 'garden',
      'garden': 'garden',
      'office products': 'office',
      'office': 'office',
      'baby': 'baby',
      'baby products': 'baby',
      'musical instruments': 'music',
      'music': 'music',
      'video games': 'video-games',
      'software': 'software',
      'computers & accessories': 'computers',
      'computers': 'computers',
      'cell phones & accessories': 'phones',
      'phones': 'phones',
      'camera & photo': 'camera',
      'camera': 'camera'
    };
    
    // Find matching category
    for (const [key, value] of Object.entries(categoryMap)) {
      if (normalizedCategory.includes(key)) {
        return `/stock-images/${value}.jpg`;
      }
    }
    
    // Default fallback
    return '/stock-images/default.jpg';
  }
  
  /**
   * Render product images with slider support
   * Uses category stock images for Amazon products without images
   */
  renderProductImages(product) {
    const imageContainer = document.querySelector('.product-image-container');
    if (!imageContainer) return;
    
    // Get all images (primary + additional if available)
    const images = [];
    if (product.imageUrl) images.push(product.imageUrl);
    if (product.additionalImages && Array.isArray(product.additionalImages)) {
      images.push(...product.additionalImages);
    }
    
    // Determine marketplace
    const marketplace = this.getProductMarketplace(product);
    const category = product.mainCategory || product.category || '';
    
    // If no images and it's an Amazon product, try category stock image
    if (images.length === 0 && marketplace === 'amazon' && category) {
      const stockImage = this.getCategoryStockImage(category);
      if (stockImage) {
        // Add stock image, but it will fallback to placeholder if it fails to load
        images.push(stockImage);
      }
    }
    
    // If still no images, use placeholder
    if (images.length === 0) {
      const productImage = document.getElementById('productImage');
      if (productImage) {
        productImage.src = this.getPlaceholderImage();
        productImage.alt = product.name || 'Product Image';
        productImage.onerror = () => {
          productImage.src = this.getPlaceholderImage();
        };
      }
      return;
    }
    
    // Store images for lightbox
    this.productImages = images;
    
    // If only one image, just set it (no slider needed)
    if (images.length === 1) {
      const productImage = document.getElementById('productImage');
      if (productImage) {
        const rawUrl = images[0];
        const highResUrl = this.getHighResImageUrl(rawUrl);
        const useHighRes = highResUrl !== rawUrl && rawUrl.startsWith && rawUrl.startsWith('http');
        productImage.src = useHighRes ? highResUrl : rawUrl;
        if (useHighRes) productImage.dataset.fallbackSrc = rawUrl;
        productImage.alt = product.name || 'Product Image';
        productImage.onerror = () => {
          if (productImage.dataset.fallbackSrc) {
            productImage.src = productImage.dataset.fallbackSrc;
            productImage.removeAttribute('data-fallback-src');
          } else {
            productImage.src = this.getPlaceholderImage();
          }
        };
        // Add click handler for zoom
        productImage.style.cursor = 'zoom-in';
        productImage.addEventListener('click', () => this.openImageLightbox(0));
      }
      // Show zoom button
      const zoomBtn = document.getElementById('imageZoomBtn');
      if (zoomBtn) {
        zoomBtn.style.display = 'block';
        zoomBtn.onclick = () => this.openImageLightbox(0);
      }
      // Remove any existing slider
      const existingSlider = imageContainer.querySelector('.image-slider');
      if (existingSlider) {
        existingSlider.remove();
      }
      return;
    }
    
    // Multiple images - create slider
    const existingSlider = imageContainer.querySelector('.image-slider');
    const existingImage = document.getElementById('productImage');
    
    if (existingSlider) {
      existingSlider.remove();
    }
    
    // Remove existing image if it's not part of a slider
    if (existingImage && !existingImage.closest('.image-slider')) {
      existingImage.remove();
    }
    
    const firstImgRaw = images[0];
    const firstImgHighRes = this.getHighResImageUrl(firstImgRaw);
    const firstImgUseHighRes = firstImgHighRes !== firstImgRaw && firstImgRaw.startsWith && firstImgRaw.startsWith('http');
    const firstImgSrc = firstImgUseHighRes ? firstImgHighRes : firstImgRaw;
    const firstImgFallbackAttr = firstImgUseHighRes ? ` data-fallback-src="${this.escapeHtml(firstImgRaw)}"` : '';
    const sliderHTML = `
      <div class="image-slider">
        <div class="image-slider-main">
          <img id="productImage" src="${this.escapeHtml(firstImgSrc)}"${firstImgFallbackAttr} alt="${this.escapeHtml(product.name || 'Product Image')}" class="product-detail-image slider-main-image">
          <button class="slider-nav slider-prev" aria-label="Previous image">‹</button>
          <button class="slider-nav slider-next" aria-label="Next image">›</button>
        </div>
        <div class="image-slider-thumbnails">
          ${images.map((img, idx) => `
            <button class="thumbnail-btn ${idx === 0 ? 'active' : ''}" data-index="${idx}" aria-label="View image ${idx + 1}">
              <img src="${this.escapeHtml(img)}" alt="Thumbnail ${idx + 1}" loading="lazy">
            </button>
          `).join('')}
        </div>
      </div>
    `;
    
    // Insert slider at the beginning of the container
    imageContainer.insertAdjacentHTML('afterbegin', sliderHTML);
    
    const mainImg = document.getElementById('productImage');
    if (mainImg && firstImgUseHighRes && mainImg.dataset.fallbackSrc) {
      mainImg.onerror = () => {
        if (mainImg.dataset.fallbackSrc) {
          mainImg.src = mainImg.dataset.fallbackSrc;
          mainImg.removeAttribute('data-fallback-src');
        } else {
          mainImg.src = this.getPlaceholderImage();
        }
      };
    }
    
    // Initialize slider functionality
    this.initImageSlider(images);
  }
  
  /**
   * Initialize image slider functionality
   */
  initImageSlider(images) {
    let currentIndex = 0;
    const mainImage = document.getElementById('productImage');
    const prevBtn = document.querySelector('.slider-prev');
    const nextBtn = document.querySelector('.slider-next');
    const thumbnails = document.querySelectorAll('.thumbnail-btn');
    
    const updateImage = (index) => {
      if (index < 0) index = images.length - 1;
      if (index >= images.length) index = 0;
      currentIndex = index;
      
      if (mainImage) {
        mainImage.src = images[index];
        mainImage.onerror = () => {
          mainImage.src = this.getPlaceholderImage();
        };
      }
      
      // Update thumbnail active state
      thumbnails.forEach((thumb, idx) => {
        thumb.classList.toggle('active', idx === index);
      });
    };
    
    if (prevBtn) {
      prevBtn.addEventListener('click', () => updateImage(currentIndex - 1));
    }
    
    if (nextBtn) {
      nextBtn.addEventListener('click', () => updateImage(currentIndex + 1));
    }
    
    thumbnails.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => updateImage(idx));
    });
    
    // Add click handler to main image for zoom
    if (mainImage) {
      mainImage.style.cursor = 'zoom-in';
      mainImage.addEventListener('click', () => this.openImageLightbox(currentIndex));
    }
    
    // Show zoom button
    const zoomBtn = document.getElementById('imageZoomBtn');
    if (zoomBtn) {
      zoomBtn.style.display = 'block';
      zoomBtn.onclick = () => this.openImageLightbox(currentIndex);
    }
    
    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') updateImage(currentIndex - 1);
      if (e.key === 'ArrowRight') updateImage(currentIndex + 1);
    });
  }
  
  /**
   * Open image lightbox
   */
  openImageLightbox(index) {
    if (!this.productImages || this.productImages.length === 0) return;
    
    const lightbox = document.getElementById('imageLightbox');
    const lightboxImage = document.getElementById('lightboxImage');
    if (!lightbox || !lightboxImage) return;
    
    this.lightboxIndex = Math.max(0, Math.min(index, this.productImages.length - 1));
    lightboxImage.src = this.productImages[this.lightboxIndex];
    lightboxImage.alt = this.productData?.name || 'Product Image';
    lightbox.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    
    // Show/hide navigation buttons
    const prevBtn = document.querySelector('.lightbox-prev');
    const nextBtn = document.querySelector('.lightbox-next');
    if (prevBtn) prevBtn.style.display = this.productImages.length > 1 ? 'flex' : 'none';
    if (nextBtn) nextBtn.style.display = this.productImages.length > 1 ? 'flex' : 'none';
  }
  
  /**
   * Close image lightbox
   */
  closeImageLightbox() {
    const lightbox = document.getElementById('imageLightbox');
    if (lightbox) {
      lightbox.style.display = 'none';
      document.body.style.overflow = '';
    }
  }
  
  /**
   * Navigate to previous image in lightbox
   */
  lightboxPrev() {
    if (!this.productImages || this.productImages.length === 0) return;
    this.lightboxIndex = (this.lightboxIndex - 1 + this.productImages.length) % this.productImages.length;
    const lightboxImage = document.getElementById('lightboxImage');
    if (lightboxImage) {
      lightboxImage.src = this.productImages[this.lightboxIndex];
    }
  }
  
  /**
   * Navigate to next image in lightbox
   */
  lightboxNext() {
    if (!this.productImages || this.productImages.length === 0) return;
    this.lightboxIndex = (this.lightboxIndex + 1) % this.productImages.length;
    const lightboxImage = document.getElementById('lightboxImage');
    if (lightboxImage) {
      lightboxImage.src = this.productImages[this.lightboxIndex];
    }
  }
  
  /**
   * Render buy box with price and buy button
   * Links to best (lowest) offer from all available listings
   */
  renderBuyBox(product) {
    const buyBox = document.getElementById('buyBox');
    if (!buyBox) return;
    
    // Get all listings (current + related)
    const allListings = [];
    if (this.productData) {
      allListings.push({
        ...this.productData,
        productId: this.productData.productId || this.productId || this.productData.id,
        isCurrent: true
      });
    }
    
    const relatedListings = this.relatedProducts || this.relatedListings || [];
    relatedListings.forEach(listing => {
      allListings.push({
        ...listing,
        productId: listing.productId || listing.id,
        isCurrent: false
      });
    });
    
    // Apply condition filter
    const filteredListings = this.getFilteredListings(allListings);
    
    // Find best (lowest price) offer from filtered listings
    let bestOffer = product;
    let bestPrice = this.parsePrice(product.price);
    const currentProductId = this.productData?.productId || this.productId || this.productData?.id;
    
    filteredListings.forEach(listing => {
      const listingPrice = this.parsePrice(listing.price);
      if (listingPrice && (!bestPrice || listingPrice < bestPrice)) {
        bestPrice = listingPrice;
        bestOffer = listing;
      }
    });
    
    // Use best offer for buy box
    const price = bestPrice;
    const retailPrice = bestOffer.retailPrice ? this.parsePrice(bestOffer.retailPrice) : null;
    const marketplace = this.getProductMarketplace(bestOffer);
    const marketplaceName = this.getMarketplaceName(marketplace);
    const submitterUsername = bestOffer.submitterUsername || bestOffer.submittedBy || 'UsedFindr Team';
    const rawAffiliate = bestOffer.affiliateLink || bestOffer.affiliate_link || '';
    const affiliateLink = (rawAffiliate && rawAffiliate !== '#') ? rawAffiliate : (this.getEbayItemUrlFromProductId(bestOffer.productId) || '#');
    
    // Calculate savings if retail price exists (same public-claims gate as cards)
    let savingsHTML = '';
    if (window.UF_PUBLIC_SAVINGS_CLAIMS_ENABLED === true && retailPrice && price && retailPrice > price) {
      const savingsAmount = retailPrice - price;
      const savingsPercent = Math.round((savingsAmount / retailPrice) * 100);
      savingsHTML = `
        <div class="buy-box-savings">
          <span class="savings-badge">Save $${savingsAmount.toFixed(2)} (${savingsPercent}% off)</span>
        </div>
      `;
    }

    let priceContextHTML = '';
    const priceCtx = bestOffer.price_context || bestOffer.priceContext
      || this.productData?.price_context || this.productData?.priceContext;
    if (window.UF_MARKET_PRICE_INSIGHTS_ENABLED === true && priceCtx && priceCtx.receipt) {
      const soldLink = priceCtx.sold_comps_url || priceCtx.soldCompsUrl
        ? `<a class="price-context-sold-link" href="${this.escapeHtml(priceCtx.sold_comps_url || priceCtx.soldCompsUrl)}" target="_blank" rel="noopener noreferrer sponsored">Check sold prices on eBay</a>`
        : '';
      priceContextHTML = `
        <div class="buy-box-price-context">
          <p class="price-context-receipt">${this.escapeHtml(priceCtx.receipt)}</p>
          ${soldLink}
        </div>
      `;
    } 
    const isBestPrice = bestOffer.productId !== currentProductId || filteredListings.length === 1;
    const isMasterParentView = this.productData?.isMasterParentView || false;
    const isHistorical = this.productData?.isHistorical || bestOffer.isHistorical || false;
    
    // Estimated availability: green (2+), yellow/orange (1), red (0/out)
    let availabilityHTML = '';
    const status = bestOffer.availabilityStatus || bestOffer.availability_status;
    const qty = bestOffer.quantityAvailable != null ? bestOffer.quantityAvailable : bestOffer.quantity_available;
    if (status === 'OUT_OF_STOCK') {
      availabilityHTML = '<div class="buy-box-availability buy-box-availability--out">Estimated: Out of stock</div>';
    } else if (qty != null) {
      if (qty === 0) {
        availabilityHTML = '<div class="buy-box-availability buy-box-availability--out">Estimated: Out of stock</div>';
      } else if (qty === 1) {
        availabilityHTML = '<div class="buy-box-availability buy-box-availability--low">Estimated: About 1 left</div>';
      } else {
        availabilityHTML = `<div class="buy-box-availability buy-box-availability--in">Estimated: About ${qty} left</div>`;
      }
    } else if (status === 'IN_STOCK') {
      availabilityHTML = '<div class="buy-box-availability buy-box-availability--in">Estimated: In stock</div>';
    }

    const currencyCode = bestOffer.currency || '';
    const priceWithCurrency = price ? `$${price.toFixed(2)}${currencyCode ? ' ' + this.escapeHtml(currencyCode) : ''}` : 'N/A';
    const shippingDisplay = bestOffer.shippingDisplay || bestOffer.shipping_display;
    let shippingHTML = '';
    if (shippingDisplay) {
      if (shippingDisplay === 'Free') shippingHTML = '<div class="buy-box-shipping">Free shipping</div>';
      else if (shippingDisplay === 'See listing') shippingHTML = '<div class="buy-box-shipping">Shipping: See listing</div>';
      else shippingHTML = `<div class="buy-box-shipping">+ ${this.escapeHtml(shippingDisplay)} shipping</div>`;
    }
    const sellerPct = bestOffer.sellerFeedbackPct != null ? bestOffer.sellerFeedbackPct : bestOffer.seller_feedback_pct;
    const sellerCount = bestOffer.sellerSalesCount != null ? bestOffer.sellerSalesCount : bestOffer.seller_sales_count;
    const pctNum = Number(sellerPct);
    const pctStr = (sellerPct != null && !Number.isNaN(pctNum)) ? (pctNum % 1 === 0 ? String(Math.round(pctNum)) : pctNum.toFixed(1)) : '';
    const sellerHTML = pctStr
      ? `<div class="buy-box-seller-trust">${pctStr}% positive${sellerCount != null ? ` (${sellerCount}+)` : ''}</div>`
      : '';
    const returnsAccepted = bestOffer.returnsAccepted != null ? bestOffer.returnsAccepted : bestOffer.returns_accepted;
    const returnsHTML = (returnsAccepted === true || returnsAccepted === false)
      ? `<div class="buy-box-returns">${returnsAccepted ? 'Returns accepted' : 'No returns'}</div>`
      : '';
    // Shared freshness policy (script.js loads first on this page)
    const buyFreshness = window.ufGetFreshness
      ? window.ufGetFreshness(bestOffer)
      : { state: 'live', verifiedAt: null, hoursSinceVerified: null };
    const isEnded = buyFreshness.state === 'ended' || isHistorical;
    let freshnessHTML = '';
    if (buyFreshness.hoursSinceVerified != null) {
      const hoursAgo = buyFreshness.hoursSinceVerified;
      const minutesAgo = hoursAgo * 60;
      const freshnessText = minutesAgo < 5 ? 'Updated just now' : minutesAgo < 60 ? `Price/availability as of ~${Math.round(minutesAgo)} min ago` : (
        hoursAgo < 24 ? `Price/availability as of ~${Math.round(hoursAgo)}h ago` : `Price/availability as of ${new Date(buyFreshness.verifiedAt).toLocaleDateString()}`
      );
      freshnessHTML = `<p class="buy-box-freshness" style="font-size: 0.75rem; color: var(--text-secondary, #666); margin: 0.25rem 0 0 0;">${this.escapeHtml(freshnessText)}</p>`;
    }

    // Historical / ended deal badge
    let historicalBadgeHTML = '';
    if (isEnded) {
      const lastSeenDate = this.productData?.lastSeenDate || buyFreshness.verifiedAt || bestOffer.updatedAt || bestOffer.updated_at;
      let lastSeenText = 'Last Seen Deal';
      if (lastSeenDate) {
        try {
          const date = new Date(lastSeenDate);
          const now = new Date();
          const daysAgo = Math.floor((now - date) / (1000 * 60 * 60 * 24));
          if (daysAgo === 0) lastSeenText = 'Last Seen Today';
          else if (daysAgo === 1) lastSeenText = 'Last Seen Yesterday';
          else if (daysAgo < 30) lastSeenText = `Last Seen ${daysAgo} days ago`;
          else if (daysAgo < 365) lastSeenText = `Last Seen ${Math.floor(daysAgo / 30)} months ago`;
          else lastSeenText = `Last Seen ${Math.floor(daysAgo / 365)} years ago`;
        } catch (e) {
          // Keep default text if date parsing fails
        }
      }
      historicalBadgeHTML = `
        <div class="historical-deal-badge" style="background: #f59e0b; color: white; padding: 4px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 500; margin-top: 8px;">
          ⏰ ${lastSeenText}
        </div>
      `;
    }
    
    // Best price indicator
    let bestPriceHTML = '';
    if (isMasterParentView && filteredListings.length > 1 && !isHistorical) {
      // For Master Parent views, always show "Best Deal" badge (only if not historical)
      bestPriceHTML = `
        <div class="best-price-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
            <path d="M2 17l10 5 10-5"></path>
            <path d="M2 12l10 5 10-5"></path>
          </svg>
          Best Deal
        </div>
      `;
    } else if (isBestPrice && filteredListings.length > 1 && !isHistorical) {
      bestPriceHTML = `
        <div class="best-price-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
            <path d="M2 17l10 5 10-5"></path>
            <path d="M2 12l10 5 10-5"></path>
          </svg>
          Best Price
        </div>
      `;
    }
    
    // Price comparison tooltip
    let priceComparisonHTML = '';
    if (filteredListings.length > 1) {
      const prices = filteredListings.map(l => this.parsePrice(l.price)).filter(p => p !== null);
      if (prices.length > 1) {
        const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
        const diff = avgPrice - price;
        if (diff > 0) {
          priceComparisonHTML = `
            <div class="price-comparison-tooltip" title="$${diff.toFixed(2)} cheaper than average">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              $${diff.toFixed(2)} below avg
            </div>
          `;
        }
      }
    }
    
    // Determine button text based on marketplace and historical/ended status
    let buttonText = '';
    const marketplaceLower = marketplace.toLowerCase();
    if (marketplaceLower === 'amazon' || marketplaceLower === 'ebay') {
      buttonText = isEnded ? `View on ${marketplaceName}` : `Buy on ${marketplaceName}`;
    } else {
      buttonText = `View on ${marketplaceName}`;
    }

    // Ended listings get a "Sold" treatment: no live-looking price/CTA, and a
    // "See similar" search link becomes the primary action.
    const similarQuery = this.productData?.name || bestOffer.name || '';
    const seeSimilarUrl = `search.html?q=${encodeURIComponent(similarQuery)}`;
    const soldLabelHTML = isEnded
      // "Ended", never "Sold" -- see the same note in templates/product.html.
      ? '<div class="buy-box-sold-label" style="font-weight: 600; color: var(--text-secondary, #666); margin-top: 4px;">Ended — this is the last price we saw it listed at</div>'
      : '';
    
    const buyBoxHTML = `
      <div class="buy-box-content">
        <div class="buy-box-price-section">
          <div class="buy-box-price-header">
            <div class="buy-box-price"${isEnded ? ' style="color: var(--text-secondary, #666); text-decoration: line-through;"' : ''}>${priceWithCurrency}</div>
            ${isEnded ? '' : bestPriceHTML}
          </div>
          ${soldLabelHTML}
          ${isEnded ? '' : priceComparisonHTML}
          ${isEnded ? '' : savingsHTML}
          ${isEnded ? '' : priceContextHTML}
          ${isEnded ? '' : availabilityHTML}
          ${isEnded ? '' : shippingHTML}
          ${sellerHTML}
          ${returnsHTML}
          ${freshnessHTML}
          ${historicalBadgeHTML}
        </div>
        <div class="buy-box-attribution">
          <div class="buy-box-marketplace">Listed on ${this.escapeHtml(marketplaceName)}</div>
          ${submitterUsername ? `
            <div class="buy-box-finder">
              <div class="finder-avatar">${this.escapeHtml(submitterUsername.charAt(0).toUpperCase())}</div>
              <span class="finder-name">Found by @${this.escapeHtml(submitterUsername)}</span>
            </div>
          ` : ''}
          ${isMasterParentView && filteredListings.length > 1 ? `
            <div class="buy-box-other-deals" style="margin-top: 8px; font-size: 0.875rem; color: var(--text-secondary);">
              ${filteredListings.length - 1} other deal${filteredListings.length - 1 !== 1 ? 's' : ''} available
            </div>
          ` : ''}
        </div>
        ${isEnded ? `
        <a href="${this.escapeHtml(seeSimilarUrl)}" class="buy-box-button">
          See similar listings
        </a>
        <p style="text-align: center; margin: 0.5rem 0 0 0; font-size: 0.85rem;">
          <a href="${this.escapeHtml(affiliateLink)}"
             target="_blank"
             rel="noopener noreferrer"
             data-action="navigate-external-product"
             data-product-url="${this.escapeHtml(affiliateLink)}"
             data-product-id="${this.escapeHtml(this.productId || '')}">
            ${this.escapeHtml(buttonText)}
          </a>
        </p>
        ` : `
        <a href="${this.escapeHtml(affiliateLink)}"
           target="_blank"
           rel="noopener noreferrer"
           class="buy-box-button"
           data-action="navigate-external-product"
           data-product-url="${this.escapeHtml(affiliateLink)}"
           data-product-id="${this.escapeHtml(this.productId || '')}">
          ${this.escapeHtml(buttonText)}
        </a>
        `}
        ${marketplaceLower === 'ebay' ? `
        <p class="ebay-partner-disclosure" style="font-size: 0.8rem; color: var(--text-secondary, #666); margin: 0.5rem 0 0 0;">
          As an eBay Partner, we may be compensated if you make a purchase.
        </p>
        ` : marketplaceLower === 'amazon' ? `
        <p class="amazon-associate-disclosure" style="font-size: 0.8rem; color: var(--text-secondary, #666); margin: 0.5rem 0 0 0;">
          As an Amazon Associate, we may be compensated if you make a purchase.
        </p>
        ` : ''}
        ${marketplaceLower === 'ebay' ? (() => {
          const ageHours = this.getListingAgeHours(bestOffer);
          const ageText = ageHours != null && ageHours > 6 ? this.formatListingAgeHours(ageHours) : null;
          return ageText ? `
        <p class="listing-age-disclosure" style="font-size: 0.75rem; color: var(--text-secondary, #666); margin: 0.25rem 0 0 0;">
          Listing as of ${ageText}
        </p>
          ` : '';
        })() : ''}
      </div>
    `;
    
    buyBox.innerHTML = buyBoxHTML;
  }
  
  /**
   * Render market pulse with 5% margin collapse logic
   */
  renderMarketPulse(product) {
    const marketPulse = document.getElementById('marketPulse');
    if (!marketPulse) return;
    
    // Get all listings for price comparison
    const allListings = [];
    if (this.productData) {
      allListings.push(this.productData);
    }
    
    // Add related listings
    const relatedListings = this.relatedProducts || this.relatedListings || [];
    allListings.push(...relatedListings);
    
    // Apply condition filter
    const filteredListings = this.getFilteredListings(allListings);
    
    // Extract prices from filtered listings
    const prices = filteredListings
      .map(listing => this.parsePrice(listing.price))
      .filter(price => price !== null);
    
    if (prices.length === 0) {
      marketPulse.innerHTML = '';
      return;
    }
    
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    
    // Check if prices are within 5% margin
    const priceRange = maxPrice - minPrice;
    const marginPercent = avgPrice > 0 ? (priceRange / avgPrice) * 100 : 0;
    const shouldCollapse = marginPercent <= 5 && prices.length > 0;
    
    let marketPulseHTML = '<div class="market-pulse-label">Market Pulse</div>';
    
    // If only one listing, just show the price
    if (prices.length === 1) {
      marketPulseHTML += `
        <div class="market-pulse-pills">
          <span class="price-pill price-pill-avg">Price: $${avgPrice.toFixed(2)}</span>
        </div>
      `;
    } else if (shouldCollapse) {
      // Show single "Market Price" badge when within 5% margin
      marketPulseHTML += `
        <div class="market-pulse-pills">
          <span class="price-pill price-pill-avg">Market Price: $${avgPrice.toFixed(2)}</span>
        </div>
      `;
    } else {
      // Show Low, Avg, High badges when prices vary significantly
      marketPulseHTML += `
        <div class="market-pulse-pills">
          <span class="price-pill price-pill-low">Low: $${minPrice.toFixed(2)}</span>
          <span class="price-pill price-pill-avg">Avg: $${avgPrice.toFixed(2)}</span>
          <span class="price-pill price-pill-high">High: $${maxPrice.toFixed(2)}</span>
        </div>
      `;
    }
    
    marketPulse.innerHTML = marketPulseHTML;
  }
  
  /**
   * Render deal quality score
   */
  renderDealQualityScore(product) {
    const container = document.getElementById('dealQualityScore');
    if (!container) return;
    
    // Calculate quality score based on price, discount, votes, etc.
    let score = 50; // Base score
    
    // Price factor (lower is better if retail price exists)
    if (product.retailPrice) {
      const retailPrice = this.parsePrice(product.retailPrice);
      const currentPrice = this.parsePrice(product.price);
      if (retailPrice && currentPrice && retailPrice > 0) {
        const discountPercent = ((retailPrice - currentPrice) / retailPrice) * 100;
        score += Math.min(discountPercent * 0.5, 30); // Up to 30 points for discount
      }
    }
    
    // Vote factor
    const voteScore = product.voteScore || 0;
    score += Math.min(voteScore * 2, 20); // Up to 20 points for votes
    
    score = Math.max(0, Math.min(100, Math.round(score))); // Clamp 0-100
    
    const qualityLabels = {
      90: 'Excellent',
      75: 'Great',
      60: 'Good',
      40: 'Fair',
      0: 'Poor'
    };
    
    let qualityLabel = 'Poor';
    for (const [threshold, label] of Object.entries(qualityLabels)) {
      if (score >= parseInt(threshold)) {
        qualityLabel = label;
        break;
      }
    }
    
    const percentage = (score / 100) * 100;
    const qualityColor = score >= 75 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
    
    const qualityHTML = `
      <div class="deal-quality-score-header">
        <span class="deal-quality-label">Deal Quality Score</span>
        <span class="deal-quality-value" style="color: ${qualityColor}">${score}/100 - ${qualityLabel}</span>
      </div>
      <div class="quality-bar-container">
        <div class="quality-bar">
          <div class="quality-bar-gradient"></div>
          <div class="quality-arrow" style="left: ${percentage}%; border-top-color: ${qualityColor};"></div>
        </div>
        <div class="quality-bar-labels">
          <span>Worst</span>
          <span>Best</span>
        </div>
      </div>
    `;
    
    container.innerHTML = qualityHTML;
  }
  
  /**
   * Render offers comparison table with marketplace logos
   */
  renderOffersTable() {
    const tableBody = document.getElementById('offersTableBody');
    const section = document.getElementById('relatedListingsSection');
    if (!tableBody || !section) return;
    
    // Combine current product with related listings
    const allListings = [];
    if (this.productData) {
      allListings.push({
        ...this.productData,
        productId: this.productData.productId || this.productId || this.productData.id,
        isCurrent: true
      });
    }
    
    const relatedListings = this.relatedProducts || this.relatedListings || [];
    relatedListings.forEach(listing => {
      allListings.push({
        ...listing,
        productId: listing.productId || listing.id,
        isCurrent: false
      });
    });
    
    // Apply condition filter
    let filteredListings = this.getFilteredListings(allListings);
    
    // Apply sorting
    filteredListings = this.sortListings(filteredListings);
    
    // Always show table section if there are any listings (active or historical)
    if (allListings.length > 0) {
      section.style.display = 'block';
    } else {
      section.style.display = 'none';
      return;
    }
    
    // Affiliate disclosures when offers include eBay and/or Amazon
    const hasEbayListing = filteredListings.some(l => this.getProductMarketplace(l) === 'ebay');
    const hasAmazonListing = filteredListings.some(l => this.getProductMarketplace(l) === 'amazon');
    let disclosureEl = document.getElementById('offersEbayPartnerDisclosure');
    let amazonDisclosureEl = document.getElementById('offersAmazonAssociateDisclosure');
    const header = document.getElementById('offersTableHeader');

    if (hasEbayListing) {
      if (!disclosureEl) {
        disclosureEl = document.createElement('p');
        disclosureEl.id = 'offersEbayPartnerDisclosure';
        disclosureEl.className = 'ebay-partner-disclosure';
        disclosureEl.style.fontSize = '0.8rem';
        disclosureEl.style.color = 'var(--text-secondary, #666)';
        disclosureEl.style.margin = '0.25rem 0 0.75rem 0';
        if (header && header.nextSibling) {
          section.insertBefore(disclosureEl, header.nextSibling);
        } else if (header) {
          header.after(disclosureEl);
        }
      }
      disclosureEl.textContent = 'As an eBay Partner, we may be compensated if you make a purchase.';
      disclosureEl.style.display = 'block';
    } else if (disclosureEl) {
      disclosureEl.style.display = 'none';
    }

    if (hasAmazonListing) {
      if (!amazonDisclosureEl) {
        amazonDisclosureEl = document.createElement('p');
        amazonDisclosureEl.id = 'offersAmazonAssociateDisclosure';
        amazonDisclosureEl.className = 'amazon-associate-disclosure';
        amazonDisclosureEl.style.fontSize = '0.8rem';
        amazonDisclosureEl.style.color = 'var(--text-secondary, #666)';
        amazonDisclosureEl.style.margin = '0.25rem 0 0.75rem 0';
        const insertAfter = disclosureEl && disclosureEl.style.display !== 'none'
          ? disclosureEl
          : header;
        if (insertAfter && insertAfter.nextSibling) {
          section.insertBefore(amazonDisclosureEl, insertAfter.nextSibling);
        } else if (insertAfter) {
          insertAfter.after(amazonDisclosureEl);
        } else {
          section.prepend(amazonDisclosureEl);
        }
      }
      amazonDisclosureEl.textContent = 'As an Amazon Associate, we may be compensated if you make a purchase.';
      amazonDisclosureEl.style.display = 'block';
    } else if (amazonDisclosureEl) {
      amazonDisclosureEl.style.display = 'none';
    }
    
    // Show empty state if no filtered results
    if (filteredListings.length === 0) {
      const conditionName = this.activeConditionFilter === 'all' ? 'all conditions' : this.activeConditionFilter;
      tableBody.innerHTML = `
        <tr>
          <td colspan="6" style="padding: 48px; text-align: center; color: var(--text-secondary);">
            <p style="font-size: 1rem; margin: 0;">No listings found for ${this.escapeHtml(conditionName)}.</p>
          </td>
        </tr>
      `;
      
      // Update header (reuse offersTableHeader looked up above)
      if (header) {
        header.textContent = 'Compare prices from other sources';
      }
      return;
    }
    
    // Calculate savings for each listing (compared to highest price)
    const prices = filteredListings.map(l => this.parsePrice(l.price)).filter(p => p !== null);
    const maxPrice = prices.length > 0 ? Math.max(...prices) : null;
    const minPrice = prices.length > 0 ? Math.min(...prices) : null;
    
    let tableRows = '';
    filteredListings.forEach((listing, index) => {
      const price = this.parsePrice(listing.price);
      const marketplace = this.getProductMarketplace(listing) || 'other';
      const marketplaceName = this.getMarketplaceName(marketplace);
      const condition = listing.condition || 'N/A';
      const finder = listing.submitterUsername || listing.submittedBy || 'UsedFindr Team';
      const finderInitial = finder.charAt(0).toUpperCase();
      
      // Check if listing is active or historical (shared freshness policy wins)
      const rowFreshness = window.ufGetFreshness ? window.ufGetFreshness(listing) : { state: 'live' };
      const isActive = rowFreshness.state !== 'ended'
        && (listing.isActive !== undefined ? listing.isActive : Boolean(listing.affiliateLink));
      const lastSeenDate = (rowFreshness.verifiedAt) || listing.updatedAt || listing.updated_at || listing.createdAt || listing.created_at;
      
      // eBay listing age disclosure when > 6 hours (API License 8.1(c))
      let listingAgeHTML = '';
      if (marketplace === 'ebay') {
        const ageHours = this.getListingAgeHours(listing);
        const ageText = ageHours != null && ageHours > 6 ? this.formatListingAgeHours(ageHours) : null;
        if (ageText) {
          listingAgeHTML = `<span style="display: block; font-size: 0.7rem; color: var(--text-secondary, #666); margin-top: 2px;">Listing as of ${this.escapeHtml(ageText)}</span>`;
        }
      }
      
      // Format last seen date for historical listings
      let lastSeenHTML = '';
      if (!isActive && lastSeenDate) {
        try {
          const date = new Date(lastSeenDate);
          const now = new Date();
          const daysAgo = Math.floor((now - date) / (1000 * 60 * 60 * 24));
          let lastSeenText = '';
          if (daysAgo === 0) lastSeenText = 'Today';
          else if (daysAgo === 1) lastSeenText = 'Yesterday';
          else if (daysAgo < 30) lastSeenText = `${daysAgo} days ago`;
          else if (daysAgo < 365) lastSeenText = `${Math.floor(daysAgo / 30)} months ago`;
          else lastSeenText = `${Math.floor(daysAgo / 365)} years ago`;
          lastSeenHTML = `<div style="font-size: 0.75rem; color: var(--text-secondary); margin-top: 4px;">Last seen: ${lastSeenText}</div>`;
        } catch (e) {
          // Keep empty if date parsing fails
        }
      }
      
      // Check if this is the best deal (lowest price)
      const isBestDeal = price === minPrice && prices.length > 1;
      
      // Calculate savings
      let savingsHTML = '';
      if (maxPrice && price && maxPrice > price) {
        const savings = maxPrice - price;
        const savingsPercent = Math.round((savings / maxPrice) * 100);
        savingsHTML = `<span class="savings-text">Save $${savings.toFixed(2)} (${savingsPercent}%)</span>`;
      } else {
        savingsHTML = '<span class="savings-text" style="color: var(--muted);">—</span>';
      }
      
      // Get marketplace logo/icon
      const marketplaceIcon = this.getMarketplaceIcon(marketplace);

      // Compact seller trust line ("99.6% positive (12k+) · Free returns")
      const rowSellerPct = listing.sellerFeedbackPct != null ? listing.sellerFeedbackPct : listing.seller_feedback_pct;
      const rowSellerCount = listing.sellerSalesCount != null ? listing.sellerSalesCount : listing.seller_sales_count;
      const rowReturns = listing.returnsAccepted != null ? listing.returnsAccepted : listing.returns_accepted;
      const trustParts = [];
      const rowPctNum = Number(rowSellerPct);
      if (rowSellerPct != null && !Number.isNaN(rowPctNum)) {
        const pctText = rowPctNum % 1 === 0 ? String(Math.round(rowPctNum)) : rowPctNum.toFixed(1);
        trustParts.push(`${pctText}% positive${rowSellerCount != null ? ` (${rowSellerCount}+)` : ''}`);
      }
      if (rowReturns === true) trustParts.push('Returns accepted');
      else if (rowReturns === false) trustParts.push('No returns');
      const trustLineHTML = trustParts.length > 0
        ? `<span style="display: block; font-size: 0.7rem; color: var(--text-secondary, #666); margin-top: 2px;">${this.escapeHtml(trustParts.join(' · '))}</span>`
        : '';

      // Condition badge class
      const conditionClass = `condition-${condition.toLowerCase().replace(/\s+/g, '-')}`;
      
      // Build row classes
      let rowClasses = [];
      if (listing.isCurrent) rowClasses.push('current-offer');
      if (isBestDeal) rowClasses.push('best-deal');
      
      tableRows += `
        <tr ${rowClasses.length > 0 ? `class="${rowClasses.join(' ')}"` : ''}>
          <td class="offer-source">
            <div class="marketplace-label">
              <span class="marketplace-icon">${marketplaceIcon}</span>
              <span>${this.escapeHtml(marketplaceName)}</span>
            </div>
          </td>
          <td class="offer-condition">
            <span class="condition-badge ${conditionClass}">${this.escapeHtml(condition)}</span>
            ${!isActive ? '<span style="display: block; font-size: 0.75rem; color: #f59e0b; margin-top: 4px;">Sold</span>' : ''}
            ${listingAgeHTML}
            ${lastSeenHTML}
          </td>
          <td class="offer-finder">
            <div class="finder-credit">
              <div class="finder-avatar-small">${this.escapeHtml(finderInitial)}</div>
              <span class="finder-name-small">${this.escapeHtml(finder)}</span>
            </div>
            ${trustLineHTML}
          </td>
          <td class="offer-price">${price ? `$${price.toFixed(2)}` : 'N/A'}</td>
          <td class="offer-savings">${savingsHTML}</td>
          <td class="offer-action">
            ${isBestDeal ? '<span class="best-deal-badge">Best Deal</span>' : ''}
            <a href="${this.escapeHtml(((listing.affiliateLink || listing.affiliate_link) && (listing.affiliateLink || listing.affiliate_link) !== '#') ? (listing.affiliateLink || listing.affiliate_link) : (this.getEbayItemUrlFromProductId(listing.productId || listing.product_id) || '#'))}" 
               target="_blank" 
               rel="noopener noreferrer" 
               class="view-deal-btn"
               data-action="navigate-external-product"
               data-product-url="${this.escapeHtml(((listing.affiliateLink || listing.affiliate_link) && (listing.affiliateLink || listing.affiliate_link) !== '#') ? (listing.affiliateLink || listing.affiliate_link) : (this.getEbayItemUrlFromProductId(listing.productId || listing.product_id) || '#'))}"
               data-product-id="${this.escapeHtml(this.productId || '')}">
              View Deal
            </a>
          </td>
        </tr>
      `;
    });
    
    tableBody.innerHTML = tableRows;
    
    // Update header (reuse offersTableHeader looked up above)
    if (header) {
      if (filteredListings.length <= 1) {
        header.textContent = 'Product listing details';
      } else {
        const otherCount = filteredListings.length - 1;
        header.textContent = `Compare prices from ${otherCount} other source${otherCount !== 1 ? 's' : ''}`;
      }
    }
  }
  
  /**
   * Get marketplace icon/logo HTML
   */
  getMarketplaceIcon(marketplace) {
    const normalized = String(marketplace || 'other').toLowerCase().trim();
    
    // Amazon icon (smile logo style)
    if (normalized === 'amazon') {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.5 12C6.5 8.96 8.96 6.5 12 6.5C15.04 6.5 17.5 8.96 17.5 12C17.5 15.04 15.04 17.5 12 17.5C8.96 17.5 6.5 15.04 6.5 12Z" fill="#FF9900"/>
          <path d="M12 8C9.79 8 8 9.79 8 12C8 14.21 9.79 16 12 16C14.21 16 16 14.21 16 12C16 9.79 14.21 8 12 8ZM12 14C10.9 14 10 13.1 10 12C10 10.9 10.9 10 12 10C13.1 10 14 10.9 14 12C14 13.1 13.1 14 12 14Z" fill="white"/>
          <path d="M10.5 11.5L11.5 10.5L12.5 11.5L13.5 10.5L14.5 11.5L13.5 12.5L12.5 11.5L11.5 12.5L10.5 11.5Z" fill="white"/>
        </svg>
      `;
    }
    
    // eBay icon
    if (normalized === 'ebay') {
      return `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="24" height="24" rx="4" fill="#0064D2"/>
          <text x="12" y="16" font-family="Arial, sans-serif" font-size="14" font-weight="bold" fill="white" text-anchor="middle">e</text>
        </svg>
      `;
    }
    
    // Default/Other icon
    return `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" fill="#9CA3AF" stroke="#6B7280" stroke-width="2"/>
        <text x="12" y="16" font-family="Arial, sans-serif" font-size="12" font-weight="bold" fill="white" text-anchor="middle">?</text>
      </svg>
    `;
  }
  
  /**
   * Render MasterParent product detail page
   * Implements "Best Deal" algorithm: finds lowest-priced active listing and renders as featured product
   */
  renderMasterParent(masterParent) {
    try {
      logger.info('[ProductDetail] renderMasterParent() called with:', masterParent);
      
      // Step 1: Collect ALL listings (active and historical) from all variants
      const allListings = [];
      const activeListings = [];
      const historicalListings = [];
      const variants = masterParent.variants || [];
      
      variants.forEach(variant => {
        const variantListings = variant.listings || [];
        variantListings.forEach(listing => {
          // Normalize listing data
          const normalizedListing = {
            ...listing,
            productId: listing.product_id || listing.productId,
            variantId: variant.variant_id || variant.variantId,
            variantName: variant.variant_name || variant.variantName,
            // Normalize field names for compatibility
            affiliateLink: listing.affiliate_link || listing.affiliateLink,
            imageUrl: listing.image_url || listing.imageUrl,
            submitterUsername: listing.submitted_by || listing.submitterUsername,
            sourceMarketplace: listing.marketplace || listing.source_marketplace || 'amazon',
            // Availability comes from the shared freshness policy, same as the cards
            // and the comparison table. The backend's is_active only means "has a
            // price and a link", so it can never mark a listing sold on its own.
            // Read both key shapes: getMasterParentDetail camelCases the payload.
            isActive: listingFreshness(listing).state !== 'ended'
              && Boolean((listing.affiliate_link || listing.affiliateLink) && listing.price),
            updatedAt: listing.updated_at || listing.updatedAt || listing.created_at || listing.createdAt
          };
          
          allListings.push(normalizedListing);
          
          // Separate active and historical listings
          if (normalizedListing.isActive) {
            activeListings.push(normalizedListing);
          } else {
            historicalListings.push(normalizedListing);
          }
        });
      });

      // Step 2: Find Featured Deal
      // Priority: 1) Active listings (lowest price), 2) Most recent historical listing (within 12 months)
      let featuredDeal = null;
      let isHistorical = false;
      
      if (activeListings.length > 0) {
        // Sort active listings by price (low to high)
        activeListings.sort((a, b) => {
          const priceA = this.parsePrice(a.price);
          const priceB = this.parsePrice(b.price);
          if (priceA === null) return 1;
          if (priceB === null) return -1;
          return priceA - priceB;
        });
        featuredDeal = activeListings[0];
        isHistorical = false;
      } else if (historicalListings.length > 0) {
        // Find most recent historical listing within last 12 months
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
        
        const recentHistorical = historicalListings
          .filter(listing => {
            if (!listing.updatedAt) return false;
            const updatedDate = new Date(listing.updatedAt);
            return updatedDate >= twelveMonthsAgo;
          })
          .sort((a, b) => {
            const dateA = new Date(a.updatedAt || 0);
            const dateB = new Date(b.updatedAt || 0);
            return dateB - dateA; // Most recent first
          });
        
        if (recentHistorical.length > 0) {
          featuredDeal = recentHistorical[0];
          isHistorical = true;
        } else if (historicalListings.length > 0) {
          // Fallback to most recent historical listing (even if older than 12 months)
          historicalListings.sort((a, b) => {
            const dateA = new Date(a.updatedAt || 0);
            const dateB = new Date(b.updatedAt || 0);
            return dateB - dateA; // Most recent first
          });
          featuredDeal = historicalListings[0];
          isHistorical = true;
        }
      }

      // Step 3: Create synthetic product object
      // Combine Master Parent metadata with Featured Deal's data
      const masterParentId = masterParent.master_parent_id || masterParent.id;
      const displayName = (featuredDeal && (featuredDeal.name || featuredDeal.variantName))
        || masterParent.name
        || 'Untitled Product';
      const syntheticProduct = {
        // Featured offer title, not the generic parent listing title
        name: displayName,
        description: masterParent.description || '',
        mainCategory: masterParent.mainCategory || masterParent.main_category || masterParent.category,
        subcategory: masterParent.subcategory,
        category: masterParent.mainCategory || masterParent.main_category || masterParent.category,
        
        // Featured Deal data (if available)
        price: featuredDeal?.price || null,
        condition: featuredDeal?.condition || null,
        affiliateLink: featuredDeal?.affiliateLink || featuredDeal?.affiliate_link || null,
        imageUrl: featuredDeal?.imageUrl || featuredDeal?.image_url || 
                 variants[0]?.image_url || variants[0]?.imageUrl || null,
        submitterUsername: featuredDeal?.submitterUsername || featuredDeal?.submitted_by || null,
        sourceMarketplace: featuredDeal?.sourceMarketplace || featuredDeal?.marketplace || 'amazon',
        // The buy box reads these off the best offer, which stays this synthetic
        // product whenever no listing is strictly cheaper. Without them the stock,
        // shipping and freshness lines silently vanish on every parent page.
        currency: featuredDeal?.currency || null,
        availabilityStatus: featuredDeal?.availabilityStatus ?? featuredDeal?.availability_status ?? null,
        quantityAvailable: featuredDeal?.quantityAvailable ?? featuredDeal?.quantity_available ?? null,
        shippingDisplay: featuredDeal?.shippingDisplay ?? featuredDeal?.shipping_display ?? null,
        returnsAccepted: featuredDeal?.returnsAccepted ?? featuredDeal?.returns_accepted ?? null,
        sellerFeedbackPct: featuredDeal?.sellerFeedbackPct ?? featuredDeal?.seller_feedback_pct ?? null,
        sellerSalesCount: featuredDeal?.sellerSalesCount ?? featuredDeal?.seller_sales_count ?? null,
        freshness: featuredDeal?.freshness || null,
        // Same trap as the fields above. This object replaces `this.productData`
        // before the buy box renders, so the buy box's own fallback to
        // `productData.priceContext` resolves to *this* -- and the price receipt
        // the server rendered into the HTML gets overwritten by a buy box that
        // never received it. Featured offer first, parent-level as the fallback.
        priceContext: featuredDeal?.priceContext || featuredDeal?.price_context
          || masterParent.priceContext || masterParent.price_context || null,
        // Use Master Parent ID format for productId (for saving/bookmarking the parent)
        productId: `master_parent_${masterParentId}`,
        // Store featured deal's productId separately for reference
        featuredDealProductId: featuredDeal?.product_id || featuredDeal?.productId || null,
        // Historical flag
        isHistorical: isHistorical,
        // When we last verified the listing with eBay, not when the catalog row
        // was created: refresh_stale_items saves with update_fields, so auto_now
        // never fires and updated_at stays frozen at ingest.
        lastSeenDate: (featuredDeal ? listingFreshness(featuredDeal).verifiedAt : null)
          || featuredDeal?.updatedAt || null,
        
        // Store all listings for comparison
        allListings: allListings,
        activeListings: activeListings,
        historicalListings: historicalListings,
        isMasterParentView: true,
        masterParentId: masterParentId,
        variants: variants
      };

      // Step 4: Store synthetic product and set related listings
      this.productData = syntheticProduct;
      // Include all listings (active + historical) for comparison table
      this.relatedListings = allListings.filter(l => l.productId !== featuredDeal?.productId);
      this.relatedProducts = this.relatedListings; // Alias for compatibility
      
      // Step 5: A parent with zero listings is not a 404. The historical
      // fallback above already covers "nothing live but something recent", so
      // reaching here means the parent genuinely has no offers of any age.
      // That page still has a name, an image and a category worth showing;
      // the error state claimed the product did not exist, which is both wrong
      // for the reader and a soft-404 signal on a URL we intend to keep.
      if (!featuredDeal) {
        this.renderOutOfStock(masterParent);
        return;
      }

      // Step 6: Use existing rendering methods (they work with synthetic product object)
      // Product name
      const productNameEl = document.getElementById('productName');
      if (productNameEl) {
        productNameEl.textContent = syntheticProduct.name || 'Untitled Product';
      }

      // Update breadcrumb
      this.updateBreadcrumb(syntheticProduct);

      // Product images
      this.renderProductImages(syntheticProduct);

      // Description
      if (syntheticProduct.description) {
        const productDescriptionSectionEl = document.getElementById('productDescriptionSection');
        if (productDescriptionSectionEl) {
          productDescriptionSectionEl.style.display = 'block';
          const productDescriptionEl = document.getElementById('productDescription');
          if (productDescriptionEl) {
            productDescriptionEl.textContent = syntheticProduct.description;
          }
        }
      }

      // Voting (disable for Master Parent views or show aggregate)
      this.renderVoting({
        voteScore: 0, // Master Parents don't have votes
        currentUserVote: null
      });

      // Buy box
      this.renderBuyBox(syntheticProduct);

      // DEPRECATED: Market Pulse graph feature temporarily disabled
      // Market pulse
      // this.renderMarketPulse(syntheticProduct);

      // Deal quality score
      this.renderDealQualityScore(syntheticProduct);

      // Related listings table (always show if there are any listings)
      if (allListings.length > 0) {
        this.renderRelatedListings();
        this.renderOffersTable();
        this.updateFilterCount();
      }

      // Update metadata for SEO
      this.updateMetadata();
      
      // Ensure content is shown after rendering completes
      this.showContent();
      
      logger.info('[ProductDetail] renderMasterParent() completed successfully');

    } catch (error) {
      logger.error('[ProductDetail] Error rendering MasterParent:', error);
      this.showError('Failed to load product details. Please try again later.');
    }
  }
  
  /**
   * Render "Out of Stock" state for Master Parent with no active listings
   */
  renderOutOfStock(masterParent) {
    try {
      const variants = masterParent.variants || [];
      const displayName = variants[0]?.variant_name || variants[0]?.variantName
        || masterParent.name || 'Unknown Product';

      // Set product name
      const nameElement = document.getElementById('productName');
      if (nameElement) {
        nameElement.textContent = displayName;
      }

      // Set description
      const descElement = document.getElementById('productDescription');
      if (descElement && masterParent.description) {
        descElement.textContent = masterParent.description;
        const descSection = document.getElementById('productDescriptionSection');
        if (descSection) descSection.style.display = 'block';
      }

      // Set category
      const category = masterParent.mainCategory || masterParent.main_category || masterParent.category || 'Uncategorized';
      const breadcrumbCategory = document.getElementById('breadcrumb-category');
      if (breadcrumbCategory) {
        breadcrumbCategory.textContent = category;
      }

      // Update breadcrumb
      this.updateBreadcrumb({
        name: displayName,
        mainCategory: category,
        category: category
      });

      // Render product image (use first variant image if available)
      if (variants.length > 0) {
        const firstVariant = variants[0];
        const imageUrl = firstVariant.imageUrl || firstVariant.image_url;
        if (imageUrl) {
          const productImage = document.getElementById('productImage');
          if (productImage) {
            productImage.src = imageUrl;
            productImage.alt = displayName;
            productImage.onerror = () => {
              productImage.src = this.getPlaceholderImage();
            };
          }
        } else {
          const productImage = document.getElementById('productImage');
          if (productImage) {
            productImage.src = this.getPlaceholderImage();
            productImage.alt = displayName;
          }
        }
      }

      // Disable voting buttons
      const upvoteBtn = document.getElementById('upvoteBtn');
      const downvoteBtn = document.getElementById('downvoteBtn');
      if (upvoteBtn) upvoteBtn.disabled = true;
      if (downvoteBtn) downvoteBtn.disabled = true;

      // Render "Out of Stock" buy box
      const buyBox = document.getElementById('buyBox');
      if (buyBox) {
        buyBox.innerHTML = `
          <div class="buy-box-content">
            <div class="buy-box-price-section">
              <div class="buy-box-price-header">
                <div class="buy-box-price" style="color: var(--text-secondary);">No Active Deals</div>
              </div>
            </div>
            <div class="buy-box-attribution">
              <div class="buy-box-marketplace" style="color: var(--text-secondary);">
                Currently out of stock
              </div>
            </div>
            <div class="out-of-stock-badge" style="
              padding: 16px;
              text-align: center;
              background: var(--bg-secondary);
              border: 2px dashed var(--border-color);
              border-radius: 8px;
              color: var(--text-secondary);
              font-weight: 500;
              margin-top: 16px;
            ">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin: 0 auto 8px; display: block;">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              No active listings available at this time
            </div>
          </div>
        `;
      }

      // Clear market pulse
      const marketPulse = document.getElementById('marketPulse');
      if (marketPulse) {
        marketPulse.innerHTML = '';
      }

      // Clear deal quality score
      const dealQualityScore = document.getElementById('dealQualityScore');
      if (dealQualityScore) {
        dealQualityScore.innerHTML = '';
      }

      // Hide related listings section
      const relatedListingsSection = document.getElementById('relatedListingsSection');
      if (relatedListingsSection) {
        relatedListingsSection.style.display = 'none';
      }

      // Update metadata
      this.updateMetadata();
      
      // Ensure content is shown
      this.showContent();
      
      logger.info('[ProductDetail] renderOutOfStock() completed successfully');

    } catch (error) {
      logger.error('[ProductDetail] Error rendering out of stock state:', error);
      this.showError('Failed to load product details. Please try again later.');
    }
  }

  /**
   * Render listings for a variant
   */
  renderVariantListings(listings) {
    // This would render the listings in a comparison view
    // For now, just log - can be enhanced later
    logger.info('Rendering listings:', listings);
  }
  
  /**
   * Select a variant (for MasterParent view)
   */
  selectVariant(variantId) {
    const masterParent = this.productData;
    if (!masterParent || !masterParent.variants) return;
    
    const variant = masterParent.variants.find(v => (v.variantId || v.variant_id) === variantId);
    if (variant && variant.listings) {
      this.renderVariantListings(variant.listings);
    }
  }
  
  /**
   * Helper: Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    if (text === null || text === undefined) return '';
    // Escapes quotes as well as angle brackets: this helper feeds double-quoted
    // HTML attributes, where an unescaped quote closes the attribute early.
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Upgrade eBay image URL to high-res (s-l1600) when pattern matches; fallback to original on 404.
   * @param {string} url - image URL
   * @returns {string}
   */
  getHighResImageUrl(url) {
    if (!url || typeof url !== 'string') return url;
    if (url.includes('s-l64.jpg')) return url.replace('s-l64.jpg', 's-l1600.jpg');
    if (url.includes('s-l225.jpg')) return url.replace('s-l225.jpg', 's-l1600.jpg');
    if (url.includes('s-l500.jpg')) return url.replace('s-l500.jpg', 's-l1600.jpg');
    return url;
  }

  /**
   * Helper: Get placeholder image
   */
  getPlaceholderImage() {
    // Better placeholder with icon pattern
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZTBlNGU5IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIzMDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjZjlmYWZiIi8+PHJlY3Qgd2lkdGg9IjMwMCIgaGVpZ2h0PSIzMDAiIGZpbGw9InVybCgjZ3JpZCkiLz48ZyB0cmFuc2Zvcm09InRyYW5zbGF0ZSgxNTAsMTIwKSI+PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjOWNhM2FmIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PHBhdGggZD0iTTIwIDIxdi0yYTQgNCAwIDAgMC00LTRINGE0IDQgMCAwIDAtNCA0djIiLz48Y2lyY2xlIGN4PSIxMiIgY3k9IjciIHI9IjQiLz48L3N2Zz48L2c+PHRleHQgeD0iNTAlIiB5PSI3MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzljYTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
  }
  
  /**
   * Helper: Parse price value from various formats
   * @param {string|number} priceValue - Price value (can be string with currency, number, etc.)
   * @returns {number|null} Parsed price as number, or null if invalid
   */
  parsePrice(priceValue) {
    if (priceValue === null || priceValue === undefined) return null;
    
    // If already a number, validate it
    if (typeof priceValue === 'number') {
      return isNaN(priceValue) || priceValue <= 0 ? null : priceValue;
    }
    
    // If string, extract numeric value
    if (typeof priceValue === 'string') {
      const priceStr = priceValue.replace(/[^0-9.]/g, '');
      if (!priceStr) return null;
      
      const price = parseFloat(priceStr);
      return isNaN(price) || price <= 0 ? null : price;
    }
    
    return null;
  }
  
  /**
   * Helper: Escape HTML
   */
  escapeHtml(str) {
    if (str === null || str === undefined) return '';
    // Escapes quotes as well as angle brackets: this helper feeds double-quoted
    // HTML attributes, where an unescaped quote closes the attribute early.
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

// Initialize when DOM is ready
logger.info('[ProductDetail] Script loaded, readyState:', document.readyState);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    logger.info('[ProductDetail] DOMContentLoaded, initializing ProductDetailManager...');
    try {
      window.productDetailManager = new ProductDetailManager();
      logger.info('[ProductDetail] ProductDetailManager initialized successfully');
    } catch (error) {
      logger.error('[ProductDetail] Error initializing ProductDetailManager:', error);
    }
  });
} else {
  logger.info('[ProductDetail] DOM already ready, initializing ProductDetailManager immediately...');
  try {
    window.productDetailManager = new ProductDetailManager();
    logger.info('[ProductDetail] ProductDetailManager initialized successfully');
  } catch (error) {
    logger.error('[ProductDetail] Error initializing ProductDetailManager:', error);
  }
}

